import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWebNotification } from '../hooks/useWebNotification';
import { toast } from 'sonner';
import axios from 'axios';
import { setAxiosAuthToken, refreshAccessToken } from '../utils/authClient';
import MediaPicker from '../components/MediaPicker';
import StickerImage from '../components/StickerImage';
import ChatImage from '../components/ChatImage';
import { sortMessages } from '../utils/messageOrder';
import { isStickerMessage } from '../utils/stickers';
import { renderMessageText } from '../utils/messageFormatting';
import StatusViewer from '../components/StatusViewer';
import {
  connectSocket,
  disconnectSocket,
  joinRoom,
  leaveRoom,
  sendMessage as sendSocketMessage,
  sendTypingIndicator,
  markMessagesRead,
  onNewMessage,
  onMessageEdited,
  onMessageDeleted,
  onUserTyping,
  onUserOnline,
  onUserOffline,
  onMessagesRead,
  offNewMessage,
  offMessageEdited,
  offMessageDeleted,
  offUserTyping,
  offUserOnline,
  offUserOffline,
  offMessagesRead,
  authenticateRealtime
} from '../utils/socket';

import { 
  PaperPlaneRight, 
  Image as ImageIcon, 
  Microphone, 
  ArrowLeft,
  Trash,
  Copy,
  Smiley,
  DotsThreeVertical,
  Plus,
  ArrowBendUpLeft,
  ShareFat,
  X,
  User,
  UserCircle,
  Headset,
  Phone,
  FileText,
  WhatsappLogo,
  TelegramLogo,
  Link,
  CaretDown,
  Star,
  PushPin,
  PencilSimple,
  Checks
} from '@phosphor-icons/react';
import MessageActionMenu, { MessageActionItem } from '../components/MessageActionMenu';
import VoiceMessage from '../components/VoiceMessage';
import ReadReceipts from '../components/ReadReceipts';
import TypingIndicator from '../components/TypingIndicator';
import OnlineStatus from '../components/OnlineStatus';
import { onVisibilityFetch } from '../utils/pollingKeepAlive';
import WhatsAppInput from '../components/WhatsAppInput';
import AudioCallScreen from '../components/AudioCallScreen';
import socket from '../utils/socket';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const isVideoUrl = (url) => {
  if (!url) return false;
  const videoExts = ['.mp4', '.mov', '.avi', '.webm', '.mkv'];
  const lower = url.split('?')[0].toLowerCase();
  return videoExts.some(ext => lower.endsWith(ext));
};

const buildMediaUrl = (url) => {
  if (!url) return '';
  if (typeof url !== 'string') return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:') || url.startsWith('data:')) return url;
  const cleanUrl = url.startsWith('/') ? url : `/${url}`;
  if (cleanUrl.startsWith('/api/')) return `${BACKEND_URL}${cleanUrl}`;
  if (cleanUrl.startsWith('/uploads/')) return `${BACKEND_URL}${cleanUrl}`;
  return `${API}${cleanUrl}`;
};


// Props:
// chatType - override chat type (for embedded mode)
// isEmbedded - when true, adjusts styling for split screen
// onBack - callback for back button in embedded mode
const CustomerChat = ({ chatType: propChatType, isEmbedded = false, onBack }) => {
  const [searchParams] = useSearchParams();
  const chatType = propChatType || searchParams.get('type') || 'deposit';
  const navigate = useNavigate();
  
  const { user: authUser, login } = useAuth();
  
  // Check for inquiry guest session
  const [guestUser, setGuestUser] = useState(null);
  const user = guestUser || authUser;
  const userRef = useRef(user);
  useEffect(() => {
    userRef.current = user;
  }, [user]);  // Use guest user if present, otherwise auth user
  const [showLeaveChatModal, setShowLeaveChatModal] = useState(false);

  const isProtectedCustomerChat = chatType === 'inquiry' && Boolean(user?.is_guest || user?.is_inquiry || searchParams.get('guest') === 'true');

  // Keep browser/phone back inside guest inquiry chats.
  useEffect(() => {
    if (isEmbedded) return undefined;

    if (isProtectedCustomerChat) {
      const guardState = { ...(window.history.state || {}), customerChatGuard: true };
      window.history.pushState(guardState, '', window.location.href);

      const handlePopState = () => {
        setShowLeaveChatModal(true);
        window.history.pushState(guardState, '', window.location.href);
      };

      window.addEventListener('popstate', handlePopState);
      return () => window.removeEventListener('popstate', handlePopState);
    } else {
      // Regular customer chat (deposit, withdrawal, support):
      // On browser/mobile back gesture, always navigate to chat list (/customer-select), NOT back to login page
      const handlePopState = () => {
        if (user && user.role === 'customer' && !user.is_guest && !user.is_inquiry) {
          navigate('/customer-select', { replace: true });
        }
      };

      window.addEventListener('popstate', handlePopState);
      return () => window.removeEventListener('popstate', handlePopState);
    }
  }, [isProtectedCustomerChat, isEmbedded, user, navigate]);

  const handleCustomerChatBack = () => {
    if (isProtectedCustomerChat && !isEmbedded) {
      setShowLeaveChatModal(true);
      return;
    }

    if (isEmbedded && onBack) {
      onBack();
      return;
    }

    if (user && user.role === 'customer' && !user.is_guest && !user.is_inquiry) {
      navigate('/customer-select', { replace: true });
      return;
    }

    navigate('/customer-login', { replace: true });
  };

  // Deposit range options flow state
  const [depositFlowState, setDepositFlowState] = useState('idle');
  const [isSubmittingDeposit, setIsSubmittingDeposit] = useState(false);
  const [customAmount, setCustomAmount] = useState('');

  useEffect(() => {
    if (user?.id) {
      localStorage.setItem('deposit_flow_state_' + user.id, 'idle');
      setDepositFlowState('idle');
    }
  }, [user]);

  const updateDepositFlowState = (newState) => {
    setDepositFlowState(newState);
    if (user?.id) {
      localStorage.setItem('deposit_flow_state_' + user.id, newState);
    }
  };

  const getUnresolvedDepositRequestCount = () => {
    let count = 0;
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (!msg) continue;
      
      // Stop counting if we find a deposit success message from handler/admin
      const senderRole = msg.sender_role || msg.senderRole;
      const isFromHandler = ['admin', 'super_admin', 'handler', 'deposit_handler', 'withdrawal_handler', 'support_handler'].includes(senderRole);
      const isDepositSuccessText = msg.text && (
        msg.text.includes("सफलतापूर्वक जमा") || 
        msg.text.toLowerCase().includes("successfully deposited") ||
        msg.text.toLowerCase().includes("coins have been successfully deposited") ||
        msg.text.includes("जमा हो गए हैं") ||
        msg.text.includes("कृपया अपनी ID check कर लीजिए")
      );
      const isDepositSuccess = (isFromHandler && isDepositSuccessText) || (msg.message_type === 'deposit' && senderRole !== 'customer');
      if (isDepositSuccess) {
        break;
      }
      
      // Count deposit request messages from customer
      const isFromCustomer = msg.sender_role === 'customer';
      const isDepositRequestText = msg.text && (
        msg.text.includes("Hi, I want despoit. Please send QR/Bank Details") || 
        msg.text.includes("Hi, I want Deposit, Please send Qr/Bank Details") ||
        msg.text.toLowerCase().includes("i want deposit")
      );
      if (isFromCustomer && isDepositRequestText) {
        count++;
      }
    }
    return count;
  };

  const handleDepositRequestClick = async () => {
    if (!roomId) return;
    try {
      // Fetch pending count from database
      const countRes = await axios.get(`${API}/chat/room/${roomId}/pending-count`);
      const pendingCount = countRes.data.count;
      if (pendingCount >= 3) {
        alert("Please wait, your request is pending");
        return;
      }
      updateDepositFlowState('options_pending');
    } catch (err) {
      console.error("Failed to check pending count", err);
      const unresolvedCount = getUnresolvedDepositRequestCount();
      if (unresolvedCount >= 3) {
        alert("Please wait, your request is pending");
        return;
      }
      updateDepositFlowState('options_pending');
    }
  };

  const handleSendDepositRequest = async () => {
    if (isSubmittingDeposit) return;
    if (!customAmount || parseFloat(customAmount) <= 0) {
      alert("Please enter a valid amount");
      return;
    }
    const amountVal = customAmount;
    setIsSubmittingDeposit(true);
    setCustomAmount('');
    updateDepositFlowState('idle');

    try {
      // Create pending form request on backend
      const res = await axios.post(`${API}/chat/form-request/create`, {
        type: 'deposit',
        amount: parseFloat(amountVal),
        roomId: roomId
      });
      const formRequest = res.data;

      // Send the text request message, bypassing the interceptor to prevent loop
      await sendMessage(null, "Hi, I want Deposit, Please send Qr/Bank Details.", true);

      // Send the card message, bypassing the interceptor
      const username = user?.username || user?.name || 'client';
      const cardMsg = `🎫 ${formRequest.form_id} — Deposit Form\nRequested by: ${username} (client)\n@${username}\nAmount: ₹${amountVal}`;
      await sendMessage(null, cardMsg, true);

      setShowDepositQuickReply(false);
    } catch (err) {
      console.error("Deposit request submission failed", err);
      alert("Failed to submit deposit request. Please try again.");
    } finally {
      setIsSubmittingDeposit(false);
    }
  };

  // Withdrawal Form State
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showDepositQuickReply, setShowDepositQuickReply] = useState(true);
  const [showWithdrawQuickReply, setShowWithdrawQuickReply] = useState(true);
  const [savedBankDetails, setSavedBankDetails] = useState(null);
  const [isFetchingBank, setIsFetchingBank] = useState(false);

  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawHolderName, setWithdrawHolderName] = useState('');
  const [withdrawAccountNumber, setWithdrawAccountNumber] = useState('');
  const [withdrawIfsc, setWithdrawIfsc] = useState('');
  const [withdrawBankName, setWithdrawBankName] = useState('');
  const [withdrawUpi, setWithdrawUpi] = useState('');
  const [withdrawDescription, setWithdrawDescription] = useState('');
  const [isSubmittingWithdrawal, setIsSubmittingWithdrawal] = useState(false);

  const fetchSavedBankDetails = useCallback(async () => {
    if (!user?.id) return;
    setIsFetchingBank(true);
    try {
      const response = await axios.get(`${API}/client-bank-details/${user.id}`);
      setSavedBankDetails(response.data?.bank_details || null);
    } catch (err) {
      console.error("Failed to fetch saved bank details:", err);
    } finally {
      setIsFetchingBank(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (chatType === 'withdrawal' && user?.id) {
      fetchSavedBankDetails();
    }
  }, [chatType, user?.id, fetchSavedBankDetails]);

  useEffect(() => {
    if (showWithdrawModal) {
      setWithdrawAmount('');
      setWithdrawDescription('');
      if (savedBankDetails) {
        setWithdrawHolderName(savedBankDetails.account_holder_name || '');
        setWithdrawAccountNumber(savedBankDetails.bank_account_number || '');
        setWithdrawIfsc(savedBankDetails.ifsc_code || '');
        setWithdrawBankName(savedBankDetails.bank_name || '');
        setWithdrawUpi(savedBankDetails.upi_id || '');
      } else {
        setWithdrawHolderName('');
        setWithdrawAccountNumber('');
        setWithdrawIfsc('');
        setWithdrawBankName('');
        setWithdrawUpi('');
      }
    }
  }, [showWithdrawModal, savedBankDetails]);

  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const lines = text.split('\n');
      lines.forEach(line => {
        const lower = line.toLowerCase();
        if (lower.includes('name') || lower.includes('holder')) {
          const val = line.split(':')[1]?.trim() || line.split('-')[1]?.trim();
          if (val) setWithdrawHolderName(val);
        } else if (lower.includes('account') || lower.includes('a/c') || lower.includes('acc')) {
          const val = line.split(':')[1]?.trim() || line.split('-')[1]?.trim();
          if (val) setWithdrawAccountNumber(val);
        } else if (lower.includes('ifsc')) {
          const val = line.split(':')[1]?.trim() || line.split('-')[1]?.trim();
          if (val) setWithdrawIfsc(val.toUpperCase());
        } else if (lower.includes('bank')) {
          const val = line.split(':')[1]?.trim() || line.split('-')[1]?.trim();
          if (val) setWithdrawBankName(val);
        } else if (lower.includes('upi')) {
          const val = line.split(':')[1]?.trim() || line.split('-')[1]?.trim();
          if (val) setWithdrawUpi(val);
        }
      });
      toast.success("Details pasted from clipboard!");
    } catch (err) {
      toast.error("Failed to read from clipboard. Please allow clipboard permissions.");
    }
  };

  const [showAccountLockedPopup, setShowAccountLockedPopup] = useState(false);

  const handleWithdrawRequestClick = async () => {
    if (!roomId) return;
    try {
      const countRes = await axios.get(`${API}/chat/room/${roomId}/pending-count?type=withdrawal`);
      const pendingCount = countRes.data.count;
      const cooldownMins = countRes.data.cooldown_minutes || 0;
      if (pendingCount >= 1) {
        toast.error("You already have 1 open withdraw request. Wait for it to close first.");
        return;
      }
      if (cooldownMins > 0) {
        toast.error("Please wait 30 min for request new Withdrawal");
        return;
      }
      setShowWithdrawModal(true);
    } catch (err) {
      console.error("Failed to check pending count", err);
      setShowWithdrawModal(true);
    }
  };

  const handleWithdrawalSubmit = async (e) => {
    e?.preventDefault();
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    const isSaved = savedBankDetails && savedBankDetails.bank_account_number;
    setIsSubmittingWithdrawal(true);
    try {
      // Save bank details if not already saved and at least account or UPI provided
      if (!isSaved && (withdrawAccountNumber.trim() || withdrawUpi.trim())) {
        await axios.post(`${API}/client-bank-details`, {
          customer_id: user.id,
          bank_name: withdrawBankName,
          account_holder_name: withdrawHolderName,
          bank_account_number: withdrawAccountNumber,
          ifsc_code: withdrawIfsc,
          upi_id: withdrawUpi
        });
        await fetchSavedBankDetails();
      }

      // Create form_request proactively via API (like deposit)
      const res = await axios.post(`${API}/chat/form-request/create`, {
        type: 'withdrawal',
        amount: parseFloat(withdrawAmount),
        roomId: roomId
      });
      const formRequest = res.data;

      // Determine account display text and details
      const acctBank = isSaved ? (savedBankDetails.bank_name || '') : withdrawBankName;
      const acctNum = isSaved ? (savedBankDetails.bank_account_number || '') : withdrawAccountNumber;
      const acctHolder = isSaved ? (savedBankDetails.account_holder_name || '') : withdrawHolderName;
      const acctIfsc = isSaved ? (savedBankDetails.ifsc_code || '') : withdrawIfsc;
      const acctUpi = isSaved ? (savedBankDetails.upi_id || '') : withdrawUpi;
      const accountDisplay = acctBank && acctNum ? `${acctBank} · A/c ${acctNum}` : (acctUpi ? `UPI: ${acctUpi}` : 'Not Provided');

      // Send ONLY the formatted card message (DO NOT send text greeting here)
      const username = user?.username || user?.name || 'client';
      const cardMsg = `🎫 ${formRequest.form_id} — Withdraw Form\nRequested by: ${username} (client)\n@${username}\nAmount: ₹${withdrawAmount}\nAccount: ${accountDisplay}\nA/c Holder: ${acctHolder || '-'}\nIFSC: ${acctIfsc || '-'}\nUPI: ${acctUpi || '-'}`;
      await sendMessage(null, cardMsg, true);

      setShowWithdrawModal(false);
      toast.success("Withdrawal request submitted successfully!");
    } catch (err) {
      console.error("Withdrawal submission failed:", err);
      const errMsg = err.response?.data?.error || err.response?.data?.detail || "Withdrawal request failed. Please try again.";
      toast.error(errMsg);
    } finally {
      setIsSubmittingWithdrawal(false);
    }
  };

  // Support Request Form State
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [isSubmittingSupport, setIsSubmittingSupport] = useState(false);

  const handleSupportRequestClick = async () => {
    if (!roomId) return;
    try {
      const countRes = await axios.get(`${API}/chat/room/${roomId}/pending-count?type=support`);
      const pendingCount = countRes.data.count;
      if (pendingCount >= 1) {
        toast.error("You already have 1 open support request. Wait for it to close first.");
        return;
      }
      setShowSupportModal(true);
    } catch (err) {
      console.error("Failed to check pending count", err);
      setShowSupportModal(true);
    }
  };

  const handleSupportSubmit = async (queryText) => {
    if (!queryText || !queryText.trim()) return;
    setIsSubmittingSupport(true);
    try {
      const res = await axios.post(`${API}/chat/form-request/create`, {
        type: 'support',
        amount: 0,
        roomId: roomId,
        description: queryText.trim()
      });
      const formRequest = res.data;

      const username = user?.username || user?.name || 'client';
      const cardMsg = `🎫 ${formRequest.form_id} — Support R\nRequested by: (@${username})\nClient: ${username} (@${username})\nReference: ${queryText.trim()}`;
      await sendMessage(null, cardMsg, true);

      setShowSupportModal(false);
      toast.success("Support request submitted successfully!");
    } catch (err) {
      console.error("Support request submission failed:", err);
      const errMsg = err.response?.data?.error || err.response?.data?.detail || "Failed to submit support request.";
      toast.error(errMsg);
    } finally {
      setIsSubmittingSupport(false);
    }
  };

  const { showNotification, playNotificationSound, requestPermission } = useWebNotification();
  const showNotificationRef = useRef(showNotification);
  showNotificationRef.current = showNotification;
  const [roomId, setRoomId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [clearedAt, setClearedAt] = useState(null); // tracks when customer cleared their view
  const clearedAtRef = useRef(null); // ref so loadMessages callback always reads latest value
  const [loading, setLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [inputKey, setInputKey] = useState(0);
  const [longPressMenu, setLongPressMenu] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(null);
  const [show3DotMenu, setShow3DotMenu] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [editingMsg, setEditingMsg] = useState(null);
  const [editText, setEditText] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [activeCall, setActiveCall] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const [hoveredMsg, setHoveredMsg] = useState(null);
  const [pinnedMessage, setPinnedMessage] = useState(null);
  
  // Image Preview State
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [allMediaUrls, setAllMediaUrls] = useState([]);
  const [currentMediaIdx, setCurrentMediaIdx] = useState(0);
  
  // Support Menu
  const [showSupportMenu, setShowSupportMenu] = useState(false);
  const [supportInfo, setSupportInfo] = useState({ phone: null, whatsapp: null, email: null, support_link: null });
  
  // Display Settings (fetched from Admin settings)
  const [displaySettings, setDisplaySettings] = useState({
    inquiry_name: 'DWS Team',
    support_name: 'DWS Support',
    deposit_name: 'DWS Deposit',
    withdrawal_name: 'DWS Withdrawal',
    inquiry_dp: null,
    support_dp: null,
    deposit_dp: null,
    withdrawal_dp: null
  });
  
  // Status Viewer
  const [showStatusViewer, setShowStatusViewer] = useState(false);
  
  // Reply and Forward features
  const [replyingTo, setReplyingTo] = useState(null);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [availableChats, setAvailableChats] = useState([]);
  const [messageToForward, setMessageToForward] = useState(null);

  // Having-Trouble banner state (deposit/withdrawal only)
  const [showTroubleBanner, setShowTroubleBanner] = useState(false);
  const [showSoftNudge, setShowSoftNudge] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [botTyping, setBotTyping] = useState(false); // shown briefly after option click for perceived speed
  

  // Having-Trouble banner: check every 30s in deposit/withdrawal chats
  useEffect(() => {
    if (chatType !== 'deposit' && chatType !== 'withdrawal') {
      setShowTroubleBanner(false);
      setShowSoftNudge(false);
      return;
    }
    const check = () => {
      if (!messages || messages.length === 0) {
        setShowTroubleBanner(false);
        setShowSoftNudge(false);
        return;
      }
      // Find last message from customer
      let lastCustomer = null;
      let lastHandler = null;
      for (let i = messages.length - 1; i >= 0; i--) {
        const m = messages[i];
        if (!lastCustomer && m.sender_role === 'customer') lastCustomer = m;
        if (!lastHandler && ['admin', 'super_admin', 'handler', 'deposit_handler', 'withdrawal_handler', 'support_handler'].includes(m.sender_role)) lastHandler = m;
        if (lastCustomer && lastHandler) break;
      }
      if (!lastCustomer) { setShowTroubleBanner(false); setShowSoftNudge(false); return; }
      const custTime = new Date(lastCustomer.timestamp).getTime();
      const now = Date.now();
      const minutesSince = (now - custTime) / 60000;
      const handlerReplied = lastHandler && new Date(lastHandler.timestamp).getTime() > custTime;
      setShowSoftNudge(minutesSince >= 2 && minutesSince < 5 && !handlerReplied);
      setShowTroubleBanner(minutesSince >= 5 && !handlerReplied);
    };
    check();
    const timer = setInterval(check, 20000);
    return () => clearInterval(timer);
  }, [messages, chatType]);

  // Auto-resolution check: when opening a support chat, ask the backend to inject
  // "क्या समस्या हल हुई?" if handler replied and it's been quiet for 5+ min
  useEffect(() => {
    if (chatType !== 'support' || !user?.id) return;
    const t = setTimeout(() => {
      axios.post(`${API}/support/auto-resolution-check`, {
        customer_id: user.id,
        stale_minutes: 5,
      }).catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
  }, [chatType, user?.id]);

  // Fallback polling for support chat — ensures bot messages appear instantly
  // even when socket websocket falls back to slower polling transport
  useEffect(() => {
    if (chatType !== 'support' || !roomId) return;
    const poll = setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadMessagesRef.current(roomId);
      }
    }, 8000);
    return () => clearInterval(poll);
  }, [chatType, roomId]);

  const handleEscalateToSupport = async () => {
    if (!user?.id) return;
    setEscalating(true);
    try {
      const res = await axios.post(`${API}/support/escalate`, {
        customer_id: user.id,
        from_chat_type: chatType,
      });
      // Navigate to support chat (use correct customer-chat route)
      navigate(`/customer-chat?type=support`);
    } catch (e) {
      console.error('Escalate failed', e);
      alert('Escalation failed, please try again');
    } finally {
      setEscalating(false);
    }
  };

  // Quick Reply Suggestions (shown above keyboard)
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  
  // Phase 2: Advanced features
  const [handlerTyping, setHandlerTyping] = useState(false);
  const [handlerOnlineStatus, setHandlerOnlineStatus] = useState({ is_online: false, last_seen: null });
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  
  // Track which messages have already played notification sound
  const notifiedMessageIds = useRef(new Set());
  // Track recently added message IDs to prevent brief duplicates
  const recentlyAddedMsgIds = useRef(new Set());
  // Track message IDs already marked as read in this session to prevent infinite request loops
  const markedReadIdsRef = useRef(new Set());
  const [handlerId, setHandlerId] = useState(null);
  
  const messagesEndRef = useRef(null);
  const roomIdRef = useRef(null);
  const typingCheckRef = useRef(null);
  const inputRef = useRef(null);
  const formRef = useRef(null);
  const chatContainerRef = useRef(null);
  const containerRef = useRef(null);

  // Scroll-up pagination state
  const [hasMoreOlder, setHasMoreOlder] = useState(true);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const isPrependingRef = useRef(false);
  const reachedTopRef = useRef(false);
  const isLoadingOlderRef = useRef(false);
  const prevScrollHeightRef = useRef(null);
  const prevScrollTopRef = useRef(null);
  const prependedCountRef = useRef(0);
  const messagesRef = useRef([]);
  const historyRequestIdRef = useRef(0);
  const historyAbortRef = useRef(null);
  const inquiryStartInFlightRef = useRef(false);



  // Visual Viewport Keyboard Fix for PWA
  useEffect(() => {
    const handleViewportResize = () => {
      if (containerRef.current && window.visualViewport) {
        const vh = window.visualViewport.height;
        containerRef.current.style.height = `${vh}px`;
      }
    };
    
    // Apply fix on mount
    if (window.visualViewport) {
      handleViewportResize();
      window.visualViewport.addEventListener('resize', handleViewportResize);
      window.visualViewport.addEventListener('scroll', handleViewportResize);
    }
    
    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportResize);
        window.visualViewport.removeEventListener('scroll', handleViewportResize);
      }
    };
  }, []);
  const inputContainerRef = useRef(null);
  const longPressTimer = useRef(null);
  const menuRef = useRef(null);

  // Listen for incoming calls (for receiving conference invites)
  useEffect(() => {
    const handleIncomingCall = (data) => {
      if (data.caller_id !== user?.id) {
        setIncomingCall({
          callId: data.call_id,
          callerId: data.caller_id,
          callerName: data.caller_name,
          callerProfilePic: data.caller_profile_pic,
          isConference: data.is_conference || false,
          participantNames: data.participant_names || []
        });
      }
    };
    socket.on('incoming_call', handleIncomingCall);
    return () => socket.off('incoming_call', handleIncomingCall);
  }, [user]);

  const EMOJIS = ['❤️', '👍', '😂', '😮', '😢', '🙏'];

  // Date separator helpers
  const getDateLabel = (timestamp) => {
    const msgDate = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (msgDate.toDateString() === today.toDateString()) return 'Today';
    if (msgDate.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return msgDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const shouldShowDateSeparator = (msgs, idx) => {
    if (idx === 0) return true;
    return new Date(msgs[idx - 1].timestamp).toDateString() !== new Date(msgs[idx].timestamp).toDateString();
  };

  const getImageGroup = (msgs, idx) => {
    const msg = msgs[idx];
    if (isStickerMessage(msg) || !msg.image_url || msg.voice_url || msg.document_url || isVideoUrl(msg.image_url)) return null;
    // First photo of group must NOT have caption
    if (msg.caption || (msg.text && msg.text.trim() && msg.text !== '🖼️ Photo' && msg.text !== '🎥 Video')) return null;
    if (idx > 0 && !isStickerMessage(msgs[idx - 1]) && msgs[idx - 1].image_url && !msgs[idx - 1].voice_url && !msgs[idx - 1].document_url && !isVideoUrl(msgs[idx - 1].image_url) && msgs[idx - 1].sender_id === msg.sender_id && !msgs[idx - 1].caption && (!msgs[idx - 1].text || !msgs[idx - 1].text.trim() || msgs[idx - 1].text === '🖼️ Photo' || msgs[idx - 1].text === '🎥 Video')) return null;
    const group = [msg];
    for (let i = idx + 1; i < msgs.length; i++) {
      if (group.length >= 10) break;
      const nextMsg = msgs[i];
      if (isStickerMessage(nextMsg) || !nextMsg.image_url || nextMsg.voice_url || nextMsg.document_url || isVideoUrl(nextMsg.image_url) || nextMsg.sender_id !== msg.sender_id) break;
      const hasCaption = nextMsg.caption || (nextMsg.text && nextMsg.text.trim() && nextMsg.text !== '🖼️ Photo' && nextMsg.text !== '🎥 Video');
      if (!hasCaption) {
        group.push(nextMsg);
      } else {
        // Last photo with caption → include in group, then stop
        group.push(nextMsg);
        break;
      }
    }
    return group.length > 1 ? group : null;
  };

  const isPartOfImageGroup = (msgs, idx) => {
    const msg = msgs[idx];
    if (isStickerMessage(msg) || !msg.image_url || msg.voice_url || msg.document_url || isVideoUrl(msg.image_url) || idx === 0) return false;
    let groupCount = 0;
    for (let i = idx - 1; i >= Math.max(0, idx - 10); i--) {
      const prevMsg = msgs[i];
      if (!isStickerMessage(prevMsg) && prevMsg.image_url && !prevMsg.voice_url && !prevMsg.document_url && !isVideoUrl(prevMsg.image_url) && prevMsg.sender_id === msg.sender_id && !prevMsg.caption && (!prevMsg.text || !prevMsg.text.trim() || prevMsg.text === '🖼️ Photo' || prevMsg.text === '🎥 Video')) {
        groupCount++;
      } else break;
    }
    return groupCount > 0 && groupCount < 10;
  };

  // Ref to track previous messages count for notification logic
  const prevMessagesCountRef = useRef(0);
  // Flag to skip notification on initial load — stays true for 3 seconds after mount
  const isInitialLoadRef = useRef(true);
  useEffect(() => {
    isInitialLoadRef.current = true;
    const timer = setTimeout(() => { isInitialLoadRef.current = false; }, 3000);
    return () => clearTimeout(timer);
  }, []);
  
  // Stable loadMessages function - no dependencies that change frequently
  const loadMessages = useCallback(async (room, options = {}) => {
    const isInitialLoad = options.initial === true;
    if (!room) {
      console.log('⚠️ Cannot load messages: No room');
      if (isInitialLoad) setLoading(false);
      return false;
    }
    
    const requestId = ++historyRequestIdRef.current;
    historyAbortRef.current?.abort();
    const controller = new AbortController();
    historyAbortRef.current = controller;

    try {
      if (isInitialLoad) setHistoryError(null);
      const response = await axios.get(`${API}/chat/messages/${room}`, {
        params: { user_id: user?.id, limit: 25 },
        signal: controller.signal,
        timeout: 10000
      });

      if (requestId !== historyRequestIdRef.current || roomIdRef.current !== room) return false;
      
      if (response.data && Array.isArray(response.data)) {
        console.log(`✅ Loaded ${response.data.length} messages for room ${room}`);
        
        // Skip notifications during initial load period (first 3 seconds)
        if (isInitialLoadRef.current) {
          const cleanData = response.data.filter(msg => !(msg.deleted_by_admin || msg.deletedByAdmin));
          setHasMoreOlder(cleanData.length >= 25);
          if (cleanData.length < 25) {
            reachedTopRef.current = true;
          }
          prevMessagesCountRef.current = cleanData.length;
          cleanData.forEach(msg => notifiedMessageIds.current.add(msg.id));
          setMessages(prev => {
            if (!prev || prev.length === 0) return sortMessages(cleanData);
            const map = new Map();
            prev.forEach(m => { if (m && m.id) map.set(m.id, m); });
            cleanData.forEach(m => {
              if (m && m.id) {
                const existing = map.get(m.id);
                map.set(m.id, { ...existing, ...m });
              }
            });
            return sortMessages(Array.from(map.values()));
          });
          return true;
        }
        
        if (prevMessagesCountRef.current === 0) {
          setHasMoreOlder(response.data.length >= 25);
          if (response.data.length < 25) {
            reachedTopRef.current = true;
          }
        } else if (!reachedTopRef.current) {
          setHasMoreOlder(true);
        }

        
        // Check for new messages from admin/handler and show notification
        const prevMessagesLength = prevMessagesCountRef.current;
        const newMessages = response.data;
        
        if (prevMessagesLength > 0 && newMessages.length > prevMessagesLength) {
          // Get only the NEW messages that weren't there before
          const newlyAddedMessages = newMessages.slice(prevMessagesLength);
          
          // Process each new message
          newlyAddedMessages.forEach((newMsg) => {
            // Only notify if:
            // 1. Message is from admin/handler (not from customer)
            // 2. We haven't already notified for this message
            if (newMsg.sender_id !== user?.id && 
                newMsg.sender_role !== 'customer' &&
                !notifiedMessageIds.current.has(newMsg.id) &&
                document.visibilityState !== 'visible') {
              
              console.log('🔔 New message from admin/handler, showing notification for message:', newMsg.id);
              
              // Mark this message as notified
              notifiedMessageIds.current.add(newMsg.id);
              
              const senderName = newMsg.sender_role === 'admin' ? 'Admin' : 'Handler';
              const messagePreview = newMsg.message_type === 'text' 
                ? newMsg.message_text 
                : newMsg.message_type === 'image' 
                ? '📷 Photo' 
                : newMsg.message_type === 'video'
                ? '🎥 Video'
                : newMsg.message_type === 'voice'
                ? '🎤 Voice message'
                : 'New message';
              
              showNotificationRef.current({
                title: `${senderName} - DWS Vault Chat`,
                body: messagePreview,
                tag: `chat-${room}`,
                data: {
                  roomId: room,
                  senderId: newMsg.sender_id,
                  messageType: newMsg.message_type,
                  chatType: chatType
                }
              });
            }
          });
        }
        
        // Filter out messages older than when customer cleared their view or soft-deleted by admin
        let filteredMessages = response.data.filter(msg => !(msg.deleted_by_admin || msg.deletedByAdmin));
        if (clearedAtRef.current) {
          filteredMessages = filteredMessages.filter(
            (msg) => new Date(msg.timestamp || msg.created_at || msg.createdAt) > new Date(clearedAtRef.current)
          );
        }
        
        // Update ref for next comparison
        prevMessagesCountRef.current = filteredMessages.length;
        setMessages(prev => {
          if (!prev || prev.length === 0) return sortMessages(filteredMessages);
          const map = new Map();
          prev.forEach(m => {
            if (m && m.id) map.set(m.id, m);
          });
          filteredMessages.forEach(m => {
            if (m && m.id) {
              const existing = map.get(m.id);
              map.set(m.id, { ...existing, ...m });
            }
          });
          return sortMessages(Array.from(map.values()));
        });
        if (filteredMessages.length > 0) {
          setShowQuickReplies(false);
        } else {
          setShowQuickReplies(true);
        }
        return true;
      } else {
        console.warn('⚠️ Invalid response:', response.data);
        // Don't clear messages on invalid response
        if (isInitialLoad) setHistoryError('Chat history returned an invalid response.');
        return false;
      }
    } catch (error) {
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') return false;
      console.error('❌ Load messages failed:', error);
      // Don't clear existing messages on error
      if (isInitialLoad) setHistoryError('Unable to load messages. Please retry.');
      return false;
    } finally {
      if (historyAbortRef.current === controller) historyAbortRef.current = null;
      if (isInitialLoad && requestId === historyRequestIdRef.current && roomIdRef.current === room) {
        setLoading(false);
      }
    }
  }, [user?.id, chatType]); // showNotification via ref

  // Reset scroll-up pagination when switching chats
  useEffect(() => {
    setMessages([]);
    messagesRef.current = [];
    prevMsgLenRef.current = 0;
    setHasMoreOlder(true);
    setIsLoadingOlder(false);
    isLoadingOlderRef.current = false;
    isPrependingRef.current = false;
    reachedTopRef.current = false;
    prependedCountRef.current = 0;
    prevScrollHeightRef.current = null;
    prevScrollTopRef.current = null;
    markedReadIdsRef.current = new Set();
  }, [roomId, chatType]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);


  // Fetch older messages when user scrolls to top (WhatsApp-style pagination)
  const fetchOlderMessages = useCallback(async () => {
    if (isLoadingOlderRef.current || !hasMoreOlder || !roomId || !user?.id) return;

    const currentMsgs = messagesRef.current;
    if (!currentMsgs || currentMsgs.length === 0) {
      setHasMoreOlder(false);
      return;
    }

    // Find the oldest valid non-temporary message with a timestamp
    let oldest = null;
    let oldestTimestamp = null;
    for (let i = 0; i < currentMsgs.length; i++) {
      const m = currentMsgs[i];
      if (!m || m.is_internal || String(m.id).startsWith('temp_')) continue;
      const raw = m.createdAt ?? m.created_at ?? m.timestamp ?? m.updatedAt ?? m.updated_at;
      const ts = typeof raw === 'number' ? raw : (raw ? new Date(raw).getTime() : null);
      if (ts && !Number.isNaN(ts) && ts > 0) {
        oldest = m;
        oldestTimestamp = ts;
        break;
      }
    }

    if (!oldest || !oldestTimestamp) {
      setHasMoreOlder(false);
      return;
    }

    isLoadingOlderRef.current = true;
    setIsLoadingOlder(true);

    if (chatContainerRef.current) {
      prevScrollHeightRef.current = chatContainerRef.current.scrollHeight;
      prevScrollTopRef.current = chatContainerRef.current.scrollTop;
    }

    try {
      const response = await axios.get(`${API}/chat/messages/${roomId}`, {
        params: { user_id: user.id, limit: 25, before: oldestTimestamp, beforeId: oldest.id }
      });
      if (roomIdRef.current !== roomId) return;
      if (response.data && Array.isArray(response.data)) {
        // Filter deleted by admin (customer doesn't see those)
        const batch = response.data.filter(m => !(m.deleted_by_admin || m.deletedByAdmin));
        if (response.data.length < 25 || batch.length === 0) {
          setHasMoreOlder(false);
          reachedTopRef.current = true;
        }
        if (batch.length > 0) {
          isPrependingRef.current = true;
          prependedCountRef.current += 1;
          setMessages(current => {
            const existingIds = new Set(current.map(m => m.id).filter(Boolean));
            const existingTempIds = new Set(current.map(m => m.temp_id || m.tempId).filter(Boolean));
            const newBatch = batch.filter(m => {
              if (m.id && existingIds.has(m.id)) return false;
              const tId = m.temp_id || m.tempId;
              if (tId && existingTempIds.has(tId)) return false;
              return true;
            });
            if (newBatch.length > 0) {
              return sortMessages([...newBatch, ...current]);
            }
            return current;
          });
        }
      }
    } catch (err) {
      console.error('Failed to fetch older messages:', err);
    } finally {
      isLoadingOlderRef.current = false;
      setIsLoadingOlder(false);
    }
  }, [roomId, user?.id, chatType, hasMoreOlder]);


  // After prepend, restore scroll position so user stays at same visual position
  useLayoutEffect(() => {
    if (prependedCountRef.current > 0 && prevScrollHeightRef.current !== null && chatContainerRef.current) {
      const newScrollHeight = chatContainerRef.current.scrollHeight;
      const heightDiff = newScrollHeight - prevScrollHeightRef.current;
      chatContainerRef.current.scrollTop = (prevScrollTopRef.current || 0) + heightDiff;
      prevScrollHeightRef.current = null;
      prevScrollTopRef.current = null;
    }
  }, [isLoadingOlder]);

  // Handle image preview
  const [allMediaCaptions, setAllMediaCaptions] = useState([]);

  
  const handleImageClick = (imageUrl) => {
    const mediaMessages = messages.filter(m => m.image_url && !isStickerMessage(m));
    const mediaList = mediaMessages.map(m => buildMediaUrl(m.image_url));
    const captionList = mediaMessages.map(m => m.caption || m.text || '');
    setAllMediaUrls(mediaList);
    setAllMediaCaptions(captionList);
    const idx = mediaList.indexOf(imageUrl);
    setCurrentMediaIdx(idx >= 0 ? idx : 0);
    setPreviewImageUrl(imageUrl);
    setShowImagePreview(true);
  };

  const closeImagePreview = () => {
    setShowImagePreview(false);
    setPreviewImageUrl(null);
  };

  // Handle input focus - Gentle keyboard fix (avoid too many scrolls)
  const handleInputFocus = () => {
    setTimeout(() => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      }
    }, 300);
  };

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  // Scroll to bottom on initial load and when new messages arrive
  const prevMsgLenRef = useRef(0);
  useEffect(() => {
    if (messages.length > 0) {
      if (isPrependingRef.current) {
        isPrependingRef.current = false;
        prevMsgLenRef.current = messages.length;
        return;
      }
      if (prevMsgLenRef.current === 0) {
        // Initial load - scroll after DOM paints
        scrollToBottom();
        requestAnimationFrame(scrollToBottom);
        setTimeout(scrollToBottom, 100);
        setTimeout(scrollToBottom, 500);
      } else if (messages.length > prevMsgLenRef.current && !isUserScrolling) {
        // New message arrived - scroll in next paint frame (prevents bounce)
        requestAnimationFrame(scrollToBottom);
      }
      prevMsgLenRef.current = messages.length;
    }
  }, [messages.length, roomId]);

  // Detect if user is scrolling up to read old messages
  const handleScroll = () => {
    if (!chatContainerRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    
    // If user is more than 100px from bottom, they're reading old messages
    setIsUserScrolling(distanceFromBottom > 30);

    // Trigger older message fetch when near the top (WhatsApp-style pagination)
    if (scrollTop < 120) {
      fetchOlderMessages();
    }
  };

  // Visual Viewport API - Detect keyboard open/close (disabled to prevent jumps)
  useEffect(() => {
    // Disabled: This was causing screen jumps
    // Keep empty to prevent jumps while maintaining component structure
  }, []);

  // Store loadMessages in a ref to avoid dependency issues
  const loadMessagesRef = useRef(loadMessages);
  useEffect(() => {
    loadMessagesRef.current = loadMessages;
  }, [loadMessages]);

  // Fetch display settings from Admin
  useEffect(() => {
    const fetchDisplaySettings = async () => {
      try {
        const response = await axios.get(`${API}/settings/display-names`);
        if (response.data) {
          setDisplaySettings(prev => ({ ...prev, ...response.data }));
        }
      } catch (error) {
        console.log('Using default display names');
      }
    };
    fetchDisplaySettings();
  }, []);

  // Get display name based on chat type
  const getDisplayName = () => {
    if (user?.is_inquiry) return displaySettings.inquiry_name || 'DWS Team';
    switch (chatType) {
      case 'support': return displaySettings.support_name || 'DWS Support';
      case 'deposit': return displaySettings.deposit_name || 'DWS Deposit';
      case 'withdrawal': return displaySettings.withdrawal_name || 'DWS Withdrawal';
      default: return displaySettings.deposit_name || 'DWS Team';
    }
  };

  // Helper to format/resolve DP URL correctly
  const resolveDPUrl = (dp) => {
    if (!dp) return null;
    if (dp.startsWith('http://') || dp.startsWith('https://')) return dp;
    if (dp.startsWith('/dp_chat') || dp.startsWith('/icons/') || dp.startsWith('/whatsapp')) return dp;
    return `${API.replace(/\/api\/?$/, '')}${dp.startsWith('/') ? dp : '/' + dp}`;
  };

  // Get display DP based on chat type
  const getDisplayDP = () => {
    if (user?.is_inquiry || chatType === 'inquiry') return '/dp_chat.jpeg';
    switch (chatType) {
      case 'support': return displaySettings.support_dp;
      case 'deposit': return displaySettings.deposit_dp;
      case 'withdrawal': return displaySettings.withdrawal_dp;
      default: return displaySettings.deposit_dp;
    }
  };

  // Keep axios auth token synced with current user's token
  useEffect(() => {
    if (user?.token) {
      setAxiosAuthToken(user.token);
    }
  }, [user?.token]);

  // Check for inquiry guest session on mount
  useEffect(() => {
    const inquirySession = localStorage.getItem('inquiry_session');
    const dwsUser = localStorage.getItem('dws_user');
    let dwsUserData = null;
    try { if (dwsUser) dwsUserData = JSON.parse(dwsUser); } catch(e) {}

    if (inquirySession) {
      try {
        const session = JSON.parse(inquirySession);
        
        // Check if this is a pending session (needs to be created on first message)
        if (session.is_pending) {
          // Set a temporary guest user for UI display
          setGuestUser(prev => {
            if (prev && prev.is_pending && prev.ip_address === session.ip_address) return prev;
            return {
              id: null,  // No ID yet - will be created on first message
              username: 'New Inquiry',
              phone: 'PENDING',
              role: 'customer',
              is_guest: true,
              is_inquiry: true,
              is_pending: true,  // Flag to create session on first message
              ip_address: session.ip_address,
              device_id: session.device_id || localStorage.getItem('dws_device_id') || '',
              referrer: session.referrer
            };
          });
          console.log('👤 Pending inquiry guest - session will be created on first message');
        } else {
          // Prefer active token from dws_user if matching guest user id
          const activeToken = (dwsUserData && dwsUserData.id === session.guest_user_id && dwsUserData.token) 
            ? dwsUserData.token 
            : session.token;
          const activeRefreshToken = (dwsUserData && dwsUserData.id === session.guest_user_id && dwsUserData.refresh_token) 
            ? dwsUserData.refresh_token 
            : session.refresh_token;

          const parsedUser = {
            id: session.guest_user_id,
            username: session.guest_name,
            phone: session.guest_phone || `GUEST_${session.guest_name}`,
            role: 'customer',
            is_guest: true,
            is_inquiry: true,
            token: activeToken,
            refresh_token: activeRefreshToken,
            device_id: session.device_id || localStorage.getItem('dws_device_id') || '',
            firebase_token: session.firebase_token,
            session_id: session.session_id,
            is_registered: Boolean(session.is_registered)
          };
          if (activeToken) {
            setAxiosAuthToken(activeToken);
            if (!authUser || authUser.id !== session.guest_user_id || authUser.token !== activeToken) {
              login(parsedUser);
            }
          }
          setGuestUser(prev => {
            if (prev && prev.id === session.guest_user_id && prev.token === activeToken && prev.username === session.guest_name) {
              return prev;
            }
            return parsedUser;
          });
          console.log('👤 Inquiry guest detected:', session.guest_name);
        }
      } catch (err) {
        console.error('Failed to parse inquiry session:', err);
      }
    } else if (!authUser) {
      // No inquiry session and no auth user - redirect to login
      navigate('/customer-login', { replace: true });
    }
  }, [authUser?.id, authUser?.token, navigate, login]);

  useEffect(() => {
    const currentUser = userRef.current || user;
    if (!currentUser) return;

    let isSubscribed = true;
    let retryCount = 0;

    // Initialize chat
    const initChat = async () => {
      try {
        const u = userRef.current || user;
        if (!u?.id && !u?.is_pending) return;
        console.log('🔄 Initializing chat for user:', u.id, 'type:', chatType);
        
        // For pending inquiry users (no ID yet), skip chat initialization
        if (u.is_pending) {
          console.log('ℹ️ Pending inquiry user - waiting for first message');
          setLoading(false);
          setRoomId(null);
          setMessages([]);
          setShowQuickReplies(true);
          return;
        }

        if (u.token) {
          setAxiosAuthToken(u.token);
        }

        // Only reset previous room state if not already initialized
        if (!roomIdRef.current) {
          historyAbortRef.current?.abort();
          historyRequestIdRef.current += 1;
          roomIdRef.current = null;
          setRoomId(null);
          setMessages([]);
          setHistoryError(null);
          setLoading(true);
        }

        const authHeaders = u?.token ? { Authorization: `Bearer ${u.token}` } : {};

        // Parallel requests for faster loading
        const response = await axios.post(`${API}/chat/start`, {
          customer_id: u.id,
          chat_type: chatType,
          ip_address: null
        }, { headers: authHeaders });

        if (!isSubscribed) return;
        
        console.log('📦 Chat start response:', response.data);
        
        // Room might not exist yet (will be created on first message)
        if (response.data.exists && response.data.room_id) {
          const room = response.data.room_id;
          console.log('✅ Room found:', room);
          if (roomIdRef.current !== room) {
            setRoomId(room);
            roomIdRef.current = room;
          }
          
          setHistoryError(null);

          // Restore the customer's cleared_at timestamp (persists across page refreshes)
          axios.get(`${API}/chat/room/${room}/cleared-at`, { params: { user_id: u.id }, headers: authHeaders })
            .then(r => {
              if (r.data?.cleared_at && isSubscribed) {
                clearedAtRef.current = r.data.cleared_at;
                setClearedAt(r.data.cleared_at);
              }
            })
            .catch(() => {}); // silently ignore if endpoint not available

          // History is the primary initial data source. Realtime setup must not
          // make the customer stare at an empty chat.
          await loadMessagesRef.current(room, { initial: true });
          
          // Fetch pinned message for this room
          try {
            const roomData = await axios.get(`${API}/chat/room/${room}`, { headers: authHeaders });
            if (roomData.data?.pinned_message_id) {
              const msgsRes = await axios.get(`${API}/chat/messages/${room}`, { headers: authHeaders });
              const pinMsg = msgsRes.data.find(m => m.id === roomData.data.pinned_message_id);
              if (pinMsg && isSubscribed) setPinnedMessage(pinMsg);
            }
          } catch(e) {}
          
          console.log('✅ Room ready, Socket.IO will handle real-time messages:', room);
          
        } else {
          // No room for this chat type yet - First time chat
          console.log('ℹ️ No existing room for', chatType, '- will create on first message');
          setRoomId(null);
          setMessages([]);
          setLoading(false);
          setShowQuickReplies(true);
        }

      } catch (error) {
        console.error('❌ Failed to initialize chat:', error);
        if (!isSubscribed) return;
        setLoading(false);

        const status = error.response?.status;
        if (status === 401) {
          // Try refreshing token ONCE if 401
          try {
            console.log('🔄 Attempting access token refresh after 401...');
            const updated = await refreshAccessToken();
            if (updated?.token && isSubscribed) {
              setAxiosAuthToken(updated.token);
              if (guestUser) {
                setGuestUser(prev => prev ? ({ ...prev, token: updated.token, refresh_token: updated.refresh_token }) : null);
              }
              setTimeout(() => { if (isSubscribed) initChat(); }, 300);
              return;
            }
          } catch (refreshErr) {
            console.error('❌ Refresh token failed during chat init:', refreshErr);
          }

          // Fallback if token refresh failed for inquiry/guest user (only if room not already active)
          const currentU = userRef.current || user;
          if ((currentU?.is_inquiry || currentU?.is_guest) && !roomIdRef.current) {
            console.warn('⚠️ Inquiry session token invalid. Fallback to fresh inquiry session.');
            setGuestUser(prev => prev ? ({ ...prev, is_pending: true, id: null }) : null);
          }
          return;
        }

        // Retry non-401 failures up to 3 times
        const currentU = userRef.current || user;
        if (!currentU?.is_pending && retryCount < 3) {
          retryCount++;
          setTimeout(() => {
            if (isSubscribed) {
              console.log(`🔄 Retrying chat initialization (${retryCount}/3)...`);
              initChat();
            }
          }, 2000);
        }
      }
    };

    initChat();

    // Handle page visibility change - instant refresh when tab returns
    const cleanupVisibility = onVisibilityFetch(() => {
      if (roomIdRef.current) {
        loadMessagesRef.current(roomIdRef.current);
      }
    });

    return () => {
      isSubscribed = false;
      cleanupVisibility();
    };
  }, [user?.id, user?.is_pending, chatType]);

  // Socket.IO real-time messaging (alongside polling for zero-risk)
  useEffect(() => {
    if (!roomId || !user?.id) return;
    
    connectSocket(user.id);
    joinRoom(roomId, user.id);
    console.log('🔌 Socket.IO joined room:', roomId);
    
    const handleNewMessage = (data) => {
      if (data.room_id === roomId && data.message) {
        if (data.message.deleted_by_admin || data.message.deletedByAdmin) return;
        const msgId = data.message.id;
        const msgTempId = data.message.temp_id || data.message.tempId;
        notifiedMessageIds.current.add(msgId);

        // Update guest state if credentials notice received
        if (data.message.text && data.message.text.includes('Username : ') && data.message.text.includes('Password : ')) {
          const nameMatch = data.message.text.match(/^Name\s*:\s*(.+)$/m);
          const phoneMatch = data.message.text.match(/^Username\s*:\s*(.+)$/m);
          if (nameMatch && phoneMatch) {
            const newUsername = nameMatch[1].trim();
            const newPhone = phoneMatch[1].trim();
            setGuestUser(prev => {
              if (!prev) return prev;
              // Guard: If already marked as registered and details match, bail out completely to prevent re-render!
              if (prev.is_registered && prev.username === newUsername && prev.phone === newPhone) {
                return prev;
              }
              const updated = {
                ...prev,
                username: newUsername,
                phone: newPhone,
                is_registered: true
              };
              try {
                const raw = localStorage.getItem('inquiry_session');
                if (raw) {
                  const parsed = JSON.parse(raw);
                  parsed.guest_name = newUsername;
                  parsed.guest_phone = newPhone;
                  parsed.is_registered = true;
                  localStorage.setItem('inquiry_session', JSON.stringify(parsed));
                }
              } catch (e) {}
              return updated;
            });
          }
        }
        setMessages(prev => {
          const idx = prev.findIndex(m => 
            m.id === msgId || 
            (msgTempId && (m.temp_id === msgTempId || m.tempId === msgTempId || m.id === msgTempId)) ||
            (m.sender_id === data.message.sender_id && m.text === data.message.text && (String(m.id).startsWith('temp_') || m.pending))
          );
          if (idx !== -1) {
            const updated = [...prev];
            updated[idx] = { ...updated[idx], ...data.message, delivered: true, pending: false, status: 'sent' };
            return updated;
          }
          const updated = sortMessages([...prev, { ...data.message, delivered: true }]);
          if (updated.length >= 25 && !reachedTopRef.current) {
            setHasMoreOlder(true);
          }
          return updated;
        });
      }
    };
    
    const handleMessageEdited = (data) => {
      if (data.room_id === roomId && data.message) {
        if (data.message.deleted_by_admin || data.message.deletedByAdmin) {
          setMessages(prev => prev.filter(m => m.id !== data.message.id));
        } else {
          setMessages(prev => prev.map(m => m.id === data.message.id ? { ...m, ...data.message } : m));
        }
      }
    };

    const handleMessageDeleted = (data) => {
      if (data.room_id === roomId && (data.message_id || data.messageId)) {
        const delId = data.message_id || data.messageId;
        setMessages(prev => prev.filter(m => m.id !== delId));
      }
    };

    // Listen for typing indicators via Socket.IO
    const handleUserTyping = (data) => {
      if (data.room_id === roomId && data.user_id !== user.id) {
        setHandlerTyping(data.is_typing);
      }
    };
    
    // Listen for chat move (handler moved customer to different panel)
    const handleChatMoved = (data) => {
      if (data.old_room_id === roomId) {
        toast.info(`Moved to ${data.new_type} panel`);
        // Navigate directly to the new chat type (not dashboard)
        navigate(`/customer-chat?type=${data.new_type}`, { replace: true });
      }
    };
    
    // Listen for read receipts (handler read our messages → update ticks)
    const handleMessagesRead = (data) => {
      if (data.room_id === roomId && data.message_ids) {
        setMessages(prev => prev.map(m => 
          data.message_ids.includes(m.id) ? { ...m, delivered: true, read: true } : m
        ));
      }
    };
    
    onNewMessage(handleNewMessage);
    onMessageEdited(handleMessageEdited);
    onMessageDeleted(handleMessageDeleted);
    onUserTyping(handleUserTyping);
    onMessagesRead(handleMessagesRead);
    // Note: chat_moved is emitted via emitLocal in socket.js callbackSets
    const chatMovedCallbacks = window.__chatMovedCallbacks || (window.__chatMovedCallbacks = new Set());
    chatMovedCallbacks.add(handleChatMoved);
    
    return () => {
      offNewMessage(handleNewMessage);
      offMessageEdited(handleMessageEdited);
      offMessageDeleted(handleMessageDeleted);
      offUserTyping(handleUserTyping);
      offMessagesRead(handleMessagesRead);
      chatMovedCallbacks.delete(handleChatMoved);
      leaveRoom(roomId, user.id);
    };
  }, [roomId, user?.id]);


  // Firebase token refresh for inquiry/guest users.
  // Firebase custom tokens expire after 1 hour. Without re-auth, the RTDB
  // listener stops receiving new messages (permission denied) causing the
  // one-way delay where admin/handler messages only appear after page refresh.
  useEffect(() => {
    if (!user?.is_inquiry && !user?.is_guest) return; // only for inquiry/guest users
    if (!user?.refresh_token) return;

    // Re-authenticate Firebase every 50 minutes (token lasts 1 hour)
    const interval = setInterval(async () => {
      try {
        console.log('[InquiryChat] 🔄 Refreshing Firebase token for guest user...');
        const updated = await refreshAccessToken();
        if (updated?.firebase_token) {
          const ok = await authenticateRealtime(updated.firebase_token);
          console.log('[InquiryChat] Firebase re-auth:', ok ? '✅ success' : '❌ failed');
          // Re-join the room to re-attach Firebase listener after re-auth
          if (ok && roomId && user?.id) {
            joinRoom(roomId, user.id);
          }
        }
      } catch (err) {
        console.warn('[InquiryChat] Firebase token refresh failed:', err?.message);
      }
    }, 50 * 60 * 1000); // 50 minutes

    return () => clearInterval(interval);
  }, [user?.is_inquiry, user?.is_guest, user?.refresh_token, user?.id, roomId]);

  // Phase 2: Update online status

  useEffect(() => {
    if (user?.id) {
      console.log('[CustomerChat] Setting user online:', user.id);
      axios.post(`${API}/users/online-status?user_id=${user.id}`, {
        is_online: true
      })
      .then(() => console.log('[CustomerChat] ✅ Online status updated'))
      .catch(err => console.error('[CustomerChat] ❌ Online status update failed:', err));
    }
    
    return () => {
      if (user?.id) {
        console.log('[CustomerChat] Setting user offline:', user.id);
        axios.post(`${API}/users/online-status?user_id=${user.id}`, {
          is_online: false
        }).catch(err => console.error('[CustomerChat] ❌ Offline status update failed:', err));
      }
    };
  }, [user]);

  // Phase 2: Mark messages as read (only when user is actively viewing chat)
  useEffect(() => {
    if (roomId && messages.length > 0 && user?.id && document.visibilityState === 'visible') {
      const unreadIds = messages
        .filter(m => {
          if (!m.id || m.id === 'undefined' || String(m.id).startsWith('temp_') || m.pending) return false;
          if (markedReadIdsRef.current.has(m.id)) return false;
          const senderId = m.sender_id || m.senderId;
          if (!senderId || senderId === 'pending' || senderId === user.id) return false;
          const readBy = m.readBy || m.read_by || {};
          return !readBy[user.id];
        })
        .map(m => m.id);
      
      if (unreadIds.length > 0) {
        // Track immediately in ref to avoid duplicate requests on re-renders
        unreadIds.forEach(id => markedReadIdsRef.current.add(id));

        console.log('[CustomerChat] Marking messages as read:', unreadIds.length);
        markMessagesRead(roomId, unreadIds, user.id);
        axios.post(`${API}/messages/mark-read?user_id=${user.id}`, {
          message_ids: unreadIds
        })
        .then(() => console.log('[CustomerChat] ✅ Messages marked as read'))
        .catch(err => console.error('[CustomerChat] ❌ Mark read failed:', err));

        // Optimistically update local messages array so read_by is populated
        setMessages(prev => prev.map(msg => {
          if (unreadIds.includes(msg.id)) {
            const updatedReadBy = { ...(msg.read_by || msg.readBy || {}), [user.id]: true };
            return { ...msg, read_by: updatedReadBy, readBy: updatedReadBy };
          }
          return msg;
        }));
      }
    }
  }, [messages, roomId, user]);

  // Phase 2: Check typing status
  useEffect(() => {
    if (!roomId || !user?.id) return;

    typingCheckRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/chat/typing/${roomId}?user_id=${user.id}`);
        setHandlerTyping(res.data.typing_users.length > 0);
      } catch (err) {
        console.error('Typing check failed:', err);
      }
    }, 3000); // 3s — reduces server load significantly vs 500ms

    return () => {
      if (typingCheckRef.current) {
        clearInterval(typingCheckRef.current);
      }
    };
  }, [roomId, user]);

  // Fetch support info - LOAD IMMEDIATELY ON MOUNT
  useEffect(() => {
    const fetchSupportInfo = async () => {
      try {
        console.log('📞 Fetching support info...');
        // Add timestamp to prevent caching
        const response = await axios.get(`${API}/settings/support-info?t=${Date.now()}`);
        console.log('✅ Support info loaded:', response.data);
        setSupportInfo(response.data);
      } catch (err) {
        console.error('❌ Failed to fetch support info:', err);
        // Set empty defaults on error
        setSupportInfo({ phone: null, whatsapp: null, email: null, support_link: null });
      }
    };
    
    fetchSupportInfo();
  }, []); // Load once on mount, never again

  // Phase 2: Fetch handler info (once) and online status (polling)
  useEffect(() => {
    let handlerFound = false;
    
    const fetchHandlerInfo = async () => {
      if (!roomId || handlerFound) return;
      try {
        const roomRes = await axios.get(`${API}/chat/room/${roomId}`);
        const room = roomRes.data;
        const actualHandlerId = room.assigned_handler_id || room.handler_id;
        if (actualHandlerId) {
          setHandlerId(actualHandlerId);
          handlerFound = true;
        }
      } catch (err) {
        console.error('Fetch handler info failed:', err);
      }
    };

    const fetchOnlineStatus = async () => {
      try {
        const globalRes = await axios.get(`${API}/global-online-status`);
        setHandlerOnlineStatus(globalRes.data);
      } catch (err) {
        console.error('Fetch online status failed:', err);
      }
    };

    // Fetch handler info once
    fetchHandlerInfo();
    // Fetch online status immediately
    fetchOnlineStatus();
    
    // Poll handler info until found (every 10s), online status every 10s
    const interval = setInterval(() => {
      if (!handlerFound) fetchHandlerInfo();
      fetchOnlineStatus();
    }, 10000); // 10s — online status doesn't need to be instant
    
    return () => clearInterval(interval);
  }, [roomId]);

  // Heartbeat: Update own online status
  useEffect(() => {
    if (!user?.id) return;
    
    // Set online when component mounts (immediate)
    axios.post(`${API}/users/online-status?user_id=${user.id}`, {
      is_online: true
    }).catch(err => console.error('Online status update failed:', err));
    
    // Heartbeat every 15 seconds is more than enough to maintain online presence
    const heartbeat = setInterval(() => {
      axios.post(`${API}/users/online-status?user_id=${user.id}`, {
        is_online: true
      }).catch(err => console.error('Heartbeat failed:', err));
    }, 15000); // 15s instead of 2s — saves ~400 requests/min per user
    
    // Set offline when component unmounts
    return () => {
      clearInterval(heartbeat);
      axios.post(`${API}/users/online-status?user_id=${user.id}`, {
        is_online: false
      }).catch(err => console.error('Offline status update failed:', err));
    };
  }, [user]);

  const waitForRealtimeAuth = async (firebaseToken) => {
    if (!firebaseToken) return false;

    // Realtime is an enhancement; never hold API message delivery forever when
    // Firebase is unavailable or misconfigured.
    return Promise.race([
      authenticateRealtime(firebaseToken),
      new Promise(resolve => setTimeout(() => resolve(false), 5000))
    ]);
  };

  const sendMessage = async (e, textFromInput = null, bypassInterceptor = false) => {
    e?.preventDefault();
    
    // Use text from parameter (WhatsAppInput) or from inputRef (form submission)
    const messageText = textFromInput || inputRef.current?.value?.trim();
    
    if (!messageText) {
      return;
    }

    if (user?.is_pending && user?.is_inquiry && inquiryStartInFlightRef.current) {
      toast.info('Starting your inquiry session. Please wait a moment.');
      return;
    }

    // Check for deposit request message restriction and state toggle
    if (!bypassInterceptor && chatType === 'deposit' && (
      messageText.trim().toLowerCase().includes("hi, i want despoit. please send qr/bank details") ||
      messageText.trim().toLowerCase().includes("hi, i want deposit, please send qr/bank details")
    )) {
      const unresolvedCount = getUnresolvedDepositRequestCount();
      if (unresolvedCount >= 3) {
        alert("Please wait, your request is pending");
        return;
      }
      updateDepositFlowState('options_pending');
    }

    // Generate optimistic temporary message for instant rendering
    const tempId = 'temp_' + Date.now() + '_' + Math.random().toString(36).substring(2, 7);
    const optimisticMsg = {
      id: tempId,
      temp_id: tempId,
      sender_id: user?.id || 'pending',
      sender_role: 'customer',
      sender_name: user?.username || user?.name || 'Customer',
      message_type: 'text',
      text: messageText,
      created_at: new Date().toISOString(),
      timestamp: new Date().toISOString(),
      pending: true,
      status: 'pending',
      delivered: false,
      read: false
    };

    // Render message immediately on screen with clock icon
    setMessages(prev => [...prev, optimisticMsg]);
    setIsUserScrolling(false);
    requestAnimationFrame(scrollToBottom);
    setTimeout(scrollToBottom, 50);

    try {
      let currentRoomId = roomId;
      let currentUser = user;
      
      // If this is a pending inquiry session, create it NOW on first message
      if (user?.is_pending && user?.is_inquiry) {
        console.log('📝 Creating inquiry session on first message...');

        inquiryStartInFlightRef.current = true;
        let startResponse;
        try {
          startResponse = await axios.post(`${API}/inquiry/start`, {
            ip_address: user.ip_address,
            device_id: user.device_id || localStorage.getItem('dws_device_id') || '',
            referrer: user.referrer
          });
        } finally {
          inquiryStartInFlightRef.current = false;
        }
        
        const { guest_name, guest_user_id, chat_room_id, token, refresh_token, firebase_token, session_id } = startResponse.data;
        
        // Update localStorage with real session
        const session = {
          guest_name,
          guest_user_id,
          chat_room_id,
          ip_address: user.ip_address,
          device_id: user.device_id || localStorage.getItem('dws_device_id') || '',
          is_inquiry: true,
          token,
          refresh_token,
          firebase_token,
          session_id
        };
        localStorage.setItem('inquiry_session', JSON.stringify(session));
        
        // Update guest user state
        const newGuestUser = {
          id: guest_user_id,
          username: guest_name,
          phone: `GUEST_${guest_name}`,
          role: 'customer',
          is_guest: true,
          is_inquiry: true,
          device_id: user.device_id || localStorage.getItem('dws_device_id') || '',
          token,
          refresh_token,
          firebase_token,
          session_id
        };
        const realtimeReady = await waitForRealtimeAuth(firebase_token);
        if (!realtimeReady) {
          console.warn('[CustomerChat] Realtime unavailable; continuing with API messaging');
        }
        login(newGuestUser);
        setGuestUser(newGuestUser);
        currentUser = newGuestUser;
        currentRoomId = chat_room_id;
        setRoomId(currentRoomId);
        
        console.log('✅ Inquiry session created:', guest_name);
      }
      
      // If this is a pending support guest (Trouble Sign In), create on first message
      if (user?.is_pending && user?.is_support_guest) {
        console.log('📝 Creating support guest session on first message...');
        const deviceId = user.device_id || localStorage.getItem('dws_device_id') || '';
        const startResponse = await axios.post(`${API}/guest-support/start`, { device_id: deviceId });
        const { guest_user_id, guest_name, chat_room_id, token, refresh_token, firebase_token, session_id } = startResponse.data;
        const newGuestUser = {
          id: guest_user_id,
          username: guest_name,
          phone: `GUEST_${guest_name}`,
          role: 'customer',
          is_guest: true,
          is_support_guest: true,
          token,
          refresh_token,
          firebase_token,
          session_id
        };
        const realtimeReady = await waitForRealtimeAuth(firebase_token);
        if (!realtimeReady) {
          console.warn('[CustomerChat] Realtime unavailable; continuing with API messaging');
        }
        login(newGuestUser);
        setGuestUser(newGuestUser);
        currentUser = newGuestUser;
        currentRoomId = chat_room_id;
        setRoomId(currentRoomId);
        console.log('✅ Support guest session created:', guest_name);
      }
      
      // If no room exists yet (for regular customers), create it on first message
      if (!currentRoomId) {
        const roomResponse = await axios.post(`${API}/chat/create-room`, {
          customer_id: currentUser.id,
          chat_type: chatType,
          ip_address: null
        });
        
        currentRoomId = roomResponse.data.room_id;
        setRoomId(currentRoomId);
      }
        
        // Phase 2: Auto-assign handler
        try {
          await axios.post(`${API}/chat/auto-assign/${currentRoomId}`);
          console.log('✅ Handler auto-assigned');
        } catch (err) {
          console.error('Auto-assign failed:', err);
        }

      
      // Prepare message data
      const messageData = {
        room_id: currentRoomId,
        sender_id: currentUser.id,
        text: messageText,
        temp_id: tempId,
        tempId: tempId,
        image_url: null,
        voice_url: null
      };

      // Add reply reference if replying to a message
      if (replyingTo) {
        messageData.reply_to = {
          message_id: replyingTo.id,
          text: replyingTo.text || '(Media)',
          sender_name: replyingTo.sender_name
        };
      }

      const response = await axios.post(`${API}/chat/send-message`, messageData);
      
      // Clear reply state after sending
      setReplyingTo(null);
      
      // Phase 2: Stop typing indicator when message sent
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }
      if (currentRoomId) {
        sendTypingIndicator(currentRoomId, currentUser.id, false);
        axios.post(`${API}/chat/typing?user_id=${currentUser.id}`, {
          room_id: currentRoomId,
          is_typing: false
        }).catch(err => console.error('Typing update failed:', err));
      }

      // Add or replace optimistic message with real message from server
      if (response.data && response.data.id) {
        notifiedMessageIds.current.add(response.data.id);
        const serverMsg = response.data;
        const respTempId = serverMsg.temp_id || serverMsg.tempId || tempId;
        setMessages(prev => {
          const idx = prev.findIndex(m => 
            m.id === serverMsg.id || 
            (respTempId && (m.temp_id === respTempId || m.tempId === respTempId || m.id === respTempId)) ||
            (m.id === tempId || m.temp_id === tempId) ||
            (m.sender_id === serverMsg.sender_id && m.text === serverMsg.text && (String(m.id).startsWith('temp_') || m.pending))
          );
          if (idx !== -1) {
            const updated = [...prev];
            updated[idx] = { ...updated[idx], ...serverMsg, delivered: true, pending: false, status: 'sent' };
            // Also filter out any other remaining temp duplicates of this message
            return updated.filter((item, i) => i === idx || !(item.id === tempId || item.temp_id === tempId));
          }
          return [...prev, { ...serverMsg, delivered: true }];
        });
      }
      setIsUserScrolling(false);

      // In support chat, bot may auto-post options after our message.
      // Refresh a couple of times so the bot bubble appears instantly even if socket is slow.
      if (chatType === 'support' && currentRoomId) {
        setTimeout(() => loadMessagesRef.current(currentRoomId), 400);
        setTimeout(() => loadMessagesRef.current(currentRoomId), 1500);
      }

      console.log('✅ Message sent:', messageText);
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessages(prev => prev.map(m => (m.id === tempId || m.temp_id === tempId) ? { ...m, pending: false, failed: true } : m));
    }
  };

  // Message interaction handlers
  const touchStartPos = useRef(null);
  
  const handleTouchStart = (messageId, e) => {
    // Don't trigger on child interactive elements
    if (e.target.tagName === 'IMG' || e.target.tagName === 'AUDIO' || e.target.closest('audio')) {
      return;
    }

    if (!e.touches && Number.isFinite(e.clientX) && Number.isFinite(e.clientY)) {
      setLongPressMenu({
        messageId,
        x: e.clientX,
        y: e.clientY
      });
      return;
    }
    
    // Check if touches array exists and has elements
    if (!e.touches || e.touches.length === 0) {
      console.log('No touch data available');
      return;
    }
    
    // Store initial touch position
    const touch = e.touches[0];
    touchStartPos.current = {
      x: touch.clientX,
      y: touch.clientY
    };
    
    console.log('Touch start on message:', messageId, 'at', touch.clientX, touch.clientY);
    
    longPressTimer.current = setTimeout(() => {
      const pos = touchStartPos.current;
      if (!pos) return;
      console.log('Long press detected! Opening menu at:', pos.x, pos.y);
      setLongPressMenu({
        messageId,
        x: pos.x,
        y: pos.y
      });
    }, 500); // 500ms hold
  };

  const handleTouchEnd = (e) => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
      console.log('Touch ended - cleared long press timer');
    }
    touchStartPos.current = null;
  };

  const handleTouchMove = (e) => {
    // Cancel long press only if finger moves more than 10px
    if (longPressTimer.current && touchStartPos.current && e.touches && e.touches.length > 0) {
      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const deltaX = Math.abs(currentX - touchStartPos.current.x);
      const deltaY = Math.abs(currentY - touchStartPos.current.y);
      
      // If movement is more than 10px, cancel long press
      if (deltaX > 10 || deltaY > 10) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
        console.log('Touch moved too much - cancelled long press');
      }
    }
  };

  const lastTap = useRef(0);
  const handleDoubleTap = (messageId) => {
    const now = Date.now();
    if (now - lastTap.current < 300) {
      setShowEmojiPicker(messageId);
    }
    lastTap.current = now;
  };

  const handleCopyMessage = (messageText) => {
    navigator.clipboard.writeText(messageText).then(() => {
      toast.success('Copied', { duration: 1500, style: { background: 'rgba(37,211,102,0.9)', color: '#fff', fontSize: '13px', padding: '6px 16px', minHeight: 'auto' } });
    }).catch(() => {});
    setLongPressMenu(null);
  };

  const handleDeleteClick = (messageId) => {
    setMessageToDelete(messageId);
    setLongPressMenu(null);
    setShowDeleteModal(true);
  };

  const [isDeletingMessage, setIsDeletingMessage] = useState(false);

  const handleDeleteMessage = async () => {
    if (!messageToDelete) return;
    setIsDeletingMessage(true);

    try {
      await axios.delete(`${API}/chat/message/${messageToDelete}`, {
        params: { user_id: user.id, delete_for_everyone: false }
      });

      setMessages(prev => prev.filter(msg => msg.id !== messageToDelete));
      toast.success("Message deleted");
      setShowDeleteModal(false);
      setMessageToDelete(null);
    } catch (error) {
      console.error('Failed to delete message:', error);
      toast.error('Failed to delete message');
    } finally {
      setIsDeletingMessage(false);
    }
  };

  // Reply Message Handler
  const handleReplyMessage = (msg) => {
    if (!msg) return;
    setReplyingTo(msg);
    setLongPressMenu(null);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const cancelReply = () => {
    setReplyingTo(null);
  };

  // Forward Message Handler (for Handler/Admin only)
  const handleForwardMessage = async (msg) => {
    try {
      setMessageToForward(msg);
      
      // Fetch all customer chats (Handler/Admin can forward to other customers)
      const response = await axios.get(`${API}/chat/rooms`, {
        params: { user_id: user.id, role: user.role }
      });
      
      const otherChats = response.data.filter(chat => chat.id !== roomId);
      setAvailableChats(otherChats);
      setShowForwardModal(true);
      setLongPressMenu(null);
    } catch (error) {
      console.error('Failed to load chats:', error);
      toast.error('Failed to load chats');
    }
  };

  const forwardToChat = async (targetRoomId) => {
    if (!messageToForward) return;

    try {
      await axios.post(`${API}/chat/send-message`, {
        room_id: targetRoomId,
        sender_id: user.id,
        message: isStickerMessage(messageToForward) ? '' : (messageToForward.message || '📷 Forwarded media'),
        message_type: isStickerMessage(messageToForward) ? 'sticker' : undefined,
        image_url: messageToForward.image_url || null,
        voice_url: messageToForward.voice_url || null
      });

      toast.success('Message forwarded!');
      setShowForwardModal(false);
      setMessageToForward(null);
      setAvailableChats([]);
    } catch (error) {
      console.error('Failed to forward message:', error);
      toast.error('Failed to forward');
    }
  };

  const handleAddReaction = async (messageId, emoji) => {
    try {
      await axios.post(`${API}/chat/message/${messageId}/reaction`, null, {
        params: { user_id: user.id, emoji }
      });

      setMessages(messages.map(msg => {
        if (msg.id === messageId) {
          const reactions = msg.reactions || {};
          if (reactions[emoji] && reactions[emoji].includes(user.id)) {
            reactions[emoji] = reactions[emoji].filter(id => id !== user.id);
            if (reactions[emoji].length === 0) delete reactions[emoji];
          } else {
            if (!reactions[emoji]) reactions[emoji] = [];
            reactions[emoji].push(user.id);
          }
          return { ...msg, reactions };
        }
        return msg;
      }));

      setShowEmojiPicker(null);
    } catch (error) {
      console.error('Failed to add reaction:', error);
    }
  };

  const handleDeleteFullChat = async () => {
    if (!window.confirm('Clear all messages from your view? This cannot be undone.')) return;

    try {
      const response = await axios.delete(`${API}/chat/room/${roomId}/messages`, {
        params: { user_id: user.id }
      });

      if (response.data?.cleared === 'local') {
        // Customer clear: hide messages locally, they remain on server for handler
        const now = new Date().toISOString();
        clearedAtRef.current = now; // update ref immediately so polling filter takes effect
        setClearedAt(now);
        setMessages([]);
      } else {
        // Staff clear: all messages deleted
        clearedAtRef.current = null;
        setClearedAt(null);
        setMessages([]);
      }
      setShow3DotMenu(false);
      toast.success('Chat cleared!');
    } catch (error) {
      console.error('Failed to delete chat:', error);
      toast.error('Failed to delete chat');
    }
  };


  const handleMediaSelected = async (media) => {
    try {
      let currentRoomId = roomId;
      
      // If no room exists yet, create it on first message
      if (!currentRoomId) {
        const roomResponse = await axios.post(`${API}/chat/create-room`, {
          customer_id: user.id,
          chat_type: chatType,
          ip_address: null
        });
        
        currentRoomId = roomResponse.data.room_id;
        setRoomId(currentRoomId);
      }
      
      // Determine text and URL based on media type
      let messageText = '';
      let imageUrl = null;
      let voiceUrl = null;
      let documentUrl = null;
      let documentName = null;
      
      if (media.type === 'image') {
        messageText = media.caption || '';
        imageUrl = media.url;
      } else if (media.type === 'video') {
        messageText = media.caption || '🎥 Video';
        imageUrl = media.url;
      } else if (media.type === 'voice') {
        messageText = '🎤 Voice';
        voiceUrl = media.url;
      } else if (media.type === 'sticker') {
        messageText = ''; // No text for stickers, just the image
        imageUrl = media.url;
      } else if (media.type === 'document') {
        messageText = '📄 ' + (media.fileName || 'Document');
        documentUrl = media.url;
        documentName = media.fileName || 'Document';
      } else if (media.type === 'text') {
        messageText = media.text || '';
      }
      
      const response = await axios.post(`${API}/chat/send-message`, {
        room_id: currentRoomId,
        sender_id: user.id,
        text: messageText,
        image_url: imageUrl,
        voice_url: voiceUrl,
        document_url: documentUrl,
        message_type: media.type === 'sticker' ? 'sticker' : undefined,
        document_name: documentName
      });

      // Mark as notified to prevent notification sound
      notifiedMessageIds.current.add(response.data.id);
      recentlyAddedMsgIds.current.add(response.data.id);
      setTimeout(() => recentlyAddedMsgIds.current.delete(response.data.id), 3000);
      // Add locally for instant preview (loadMessages will deduplicate via server data)
      setMessages(prev => {
        if (prev.some(m => m.id === response.data.id)) return prev;
        return [...prev, response.data];
      });
      setIsUserScrolling(false);
    } catch (error) {
      console.error('Failed to send media:', error);
      toast.error('Failed to send media');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(e);
    }
  };

  // Handler for WhatsAppInput typing indicator
  const handleTypingIndicator = (isTyping) => {
    if (roomId && user?.id) {
      // Real-time Firebase RTDB typing indicator
      sendTypingIndicator(roomId, user.id, isTyping);
      
      if (isTyping) {
        if (typingTimeout) clearTimeout(typingTimeout);
        
        const timeout = setTimeout(() => {
          sendTypingIndicator(roomId, user.id, false);
        }, 3000);
        
        setTypingTimeout(timeout);
      }
    }
  };

  // Handler for WhatsAppInput send message
  const handleWhatsAppSendMessage = (text) => {
    sendMessage(null, text);
    // Hide quick replies after first message sent
    setShowQuickReplies(false);
  };

  // Loading state removed - UI renders instantly with cached/empty messages

  return (
    <div ref={containerRef} className="fixed inset-0 bg-wa-hover overflow-hidden">
      {/* Header - WhatsApp Style */}
      <div className="absolute top-0 left-0 right-0 bg-wa-panel-header border-b border-wa-border shadow-sm z-50">
        <div className="flex items-center h-14 px-2">
          {/* Back Button - Always visible */}
          <button
            onClick={handleCustomerChatBack}
            className="p-2 hover:bg-wa-hover rounded-full transition-colors text-wa-text-primary"
            aria-label="Go back"
          >
            <ArrowLeft size={24} />
          </button>
          
          {/* Profile Picture - WhatsApp Style */}
          {(user?.is_inquiry || chatType === 'inquiry') ? (
            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden ml-2 flex-shrink-0">
              <img 
                src="/dp_chat.jpeg" 
                alt="Support" 
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = '/dp_chat.jpeg'; }}
              />
            </div>
          ) : (
            <button
              onClick={() => setShowStatusViewer(true)}
              className="relative flex-shrink-0 group ml-1"
            >
              <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 p-0.5">
                <div className="w-full h-full rounded-full bg-wa-panel flex items-center justify-center overflow-hidden">
                  {getDisplayDP() ? (
                    <img 
                      src={resolveDPUrl(getDisplayDP())} 
                      alt="Support" 
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <Headset size={24} weight="fill" className="text-wa-text-primary" />
                  )}
                </div>
              </div>
            </button>
          )}
          
          {/* Name and Status - Dynamic based on settings */}
          <div className="flex-1 ml-3 min-w-0">
            <h2 className="text-base font-semibold text-wa-text-primary truncate">{getDisplayName()}</h2>
            <div className="flex items-center">
              <OnlineStatus isOnline={handlerOnlineStatus.is_online} lastSeen={handlerOnlineStatus.last_seen} showText={true} textColor="text-wa-text-secondary" textSize="text-xs" />
            </div>
          </div>

          {/* Audio Call Button - Redirect to Phone Dialpad */}
          <button
            onClick={() => {
              const phoneNumber = supportInfo?.support_phone || supportInfo?.phone;
              if (phoneNumber) {
                window.location.href = `tel:${phoneNumber}`;
              } else {
                toast.error('Support phone number not available');
              }
            }}
            className="p-2 hover:bg-wa-hover rounded-full text-wa-text-primary"
            data-testid="audio-call-btn"
          >
            <Phone size={22} />
          </button>

          {/* 3-Dot Menu */}
          <div ref={menuRef} className="relative">
            <button
              onClick={() => setShow3DotMenu(!show3DotMenu)}
              className="p-2 hover:bg-wa-hover rounded-full text-wa-text-primary"
            >
              <DotsThreeVertical size={24} />
            </button>
            
            {show3DotMenu && (
              <>
              <div className="fixed inset-0 z-40" onClick={() => setShow3DotMenu(false)} />
              <div className="absolute right-0 mt-2 w-56 bg-wa-panel rounded-lg shadow-lg py-2 z-50">
                <button
                  onClick={() => {
                    setShow3DotMenu(false);
                    setShowStatusViewer(true);
                  }}
                  className="w-full px-4 py-3 text-left hover:bg-wa-hover flex items-center gap-3 border-b"
                >
                  <User size={22} className="text-purple-600" weight="fill" />
                  <div className="flex flex-col">
                    <span className="font-semibold text-wa-text-primary">View Status</span>
                    <span className="text-xs text-wa-text-secondary">See handler status</span>
                  </div>
                </button>
                <button
                  onClick={() => {
                    setShow3DotMenu(false);
                    setShowSupportMenu(true);
                  }}
                  className="w-full px-4 py-3 text-left hover:bg-wa-hover flex items-center gap-3 border-b"
                >
                  <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                  <div className="flex flex-col">
                    <span className="font-semibold text-wa-text-primary">Support</span>
                    <span className="text-xs text-wa-text-secondary">Call, WhatsApp, Chat</span>
                  </div>
                </button>
                <button
                  onClick={handleDeleteFullChat}
                  className="w-full px-4 py-3 text-left text-red-600 hover:bg-wa-hover flex items-center gap-3"
                >
                  <Trash size={22} weight="fill" />
                  <span className="font-semibold">Clear Chat</span>
                </button>
                {chatType === 'support' && (
                  <button
                    onClick={async () => {
                      setShow3DotMenu(false);
                      try {
                        await axios.post(`${API}/support/session/close`, { customer_id: user?.id });
                        if (roomId) loadMessagesRef.current(roomId);
                      } catch (e) {
                        console.error('End chat failed', e);
                      }
                    }}
                    className="w-full px-4 py-3 text-left text-blue-600 hover:bg-wa-hover flex items-center gap-3 border-t"
                    data-testid="end-support-chat-btn"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <div className="flex flex-col">
                      <span className="font-semibold">End This Chat</span>
                      <span className="text-xs text-wa-text-secondary">Reset options for new query</span>
                    </div>
                  </button>
                )}
              </div>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Banner Bar (Deposit or Withdrawal request on top) */}
      {chatType === 'deposit' && (
        <div className="absolute left-0 right-0 z-30 select-none px-3 py-1.5 bg-wa-panel-header border-b border-wa-border" style={{ top: '56px', height: '48px' }}>
          <button
            onClick={handleDepositRequestClick}
            className="w-full h-9 bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-all text-xs tracking-wide shadow-sm"
          >
            <span>Deposit details Send QR/Scanner/Bank</span>
          </button>
        </div>
      )}
      {chatType === 'withdrawal' && (
        <div className="absolute left-0 right-0 z-30 select-none px-3 py-1.5 bg-wa-panel-header border-b border-wa-border" style={{ top: '56px', height: '48px' }}>
          <button
            onClick={handleWithdrawRequestClick}
            className="w-full h-9 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-all text-xs tracking-wide shadow-sm"
            style={{ backgroundColor: '#075e54' }}
          >
            <span>🏦 Raise Withdraw Request</span>
          </button>
        </div>
      )}
      {chatType === 'support' && (
        <div className="absolute left-0 right-0 z-30 select-none px-3 py-1.5 bg-wa-panel-header border-b border-wa-border" style={{ top: '56px', height: '48px' }}>
          <button
            onClick={handleSupportRequestClick}
            className="w-full h-9 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-all text-xs tracking-wide shadow-sm hover:opacity-95 active:opacity-85"
            style={{ backgroundColor: '#9333ea' }}
          >
            <span>🆘 Raise Support Request</span>
          </button>
        </div>
      )}

      {/* Pinned Message Banner */}
      {pinnedMessage && (
        <div 
          className="absolute left-0 right-0 bg-wa-panel border-b shadow-sm z-20 px-4 py-2 flex items-center gap-3 cursor-pointer hover:bg-wa-hover"
          style={{ top: `${56 + (chatType === 'deposit' || chatType === 'withdrawal' || chatType === 'support' ? 48 : 0)}px` }}
          onClick={() => {
            const el = document.getElementById(`msg-${pinnedMessage.id}`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }}
          data-testid="pinned-message-banner"
        >
          <PushPin size={18} className="text-blue-500 flex-shrink-0" weight="fill" />
          <div className="flex-1 min-w-0">
            <p className="text-xs text-blue-600 font-semibold">Pinned Message</p>
            <p className="text-sm text-wa-text-secondary truncate">{pinnedMessage.text || '📷 Media'}</p>
          </div>
          <button 
            onClick={async (e) => { 
              e.stopPropagation(); 
              try { await axios.delete(`${API}/chat/room/${roomId}/pin`); setPinnedMessage(null); toast('Unpinned', { duration: 1000 }); } catch(err) {} 
            }}
            className="p-1 hover:bg-wa-hover rounded-full"
          >
            <X size={16} className="text-wa-text-secondary" />
          </button>
        </div>
      )}

      {/* Chat Messages - Fixed position area between header and input */}
      <div 
        ref={chatContainerRef}
        onScroll={handleScroll}
        className="wa-chat-wallpaper absolute left-0 right-0 overflow-y-auto p-3"
        style={{
          top: `${(pinnedMessage ? 112 : 56) + (chatType === 'deposit' || chatType === 'withdrawal' ? 48 : 0)}px`,
          bottom: ((chatType !== 'deposit' && chatType !== 'withdrawal') ? showQuickReplies : (chatType === 'deposit' && depositFlowState === 'idle' && showDepositQuickReply)) ? '120px' : '56px'
        }}
      >
        <div className="space-y-[3px] pb-4" style={{ overflowAnchor: 'none' }}>
          {loading && (
            <div className="flex min-h-32 items-center justify-center text-sm text-gray-500">
              Loading messages…
            </div>
          )}
          {!loading && historyError && (
            <div className="mx-auto mt-8 max-w-xs rounded-lg bg-white/90 p-4 text-center shadow-sm">
              <p className="text-sm text-gray-600">{historyError}</p>
              {roomId && (
                <button
                  type="button"
                  onClick={() => loadMessagesRef.current(roomId, { initial: true })}
                  className="mt-3 rounded-md bg-[#008069] px-3 py-1.5 text-xs font-semibold text-white"
                >
                  Retry
                </button>
              )}
            </div>
          )}
          {/* Older messages loading spinner */}
          {isLoadingOlder && (
            <div className="flex justify-center items-center py-3">
              <div className="w-5 h-5 border-2 border-[#25D366] border-t-transparent rounded-full animate-spin" />
              <span className="ml-2 text-xs text-gray-400">Loading older messages…</span>
            </div>
          )}
          {messages.filter(msg => msg && msg.id && !msg.is_internal && !msg.isInternal && !msg.text?.includes('Call — initiated by') && !msg.text?.includes('WhatsApp redirect — by') && !msg.text?.includes('Assigned to MB') && !msg.text?.includes('MB tag removed')).map((msg, idx) => {
            const isOwn = (user?.id && msg.sender_id === user.id) || msg.sender_role === 'customer' || msg.pending || !!msg.temp_id;
            const reactions = msg.reactions || {};

            // === Bot message rendering (interactive cards with option buttons) ===
            if (msg.is_bot === true) {
              const suggestedKey = msg.bot_data?.suggested_key;

              // CTA link (open deposit/withdrawal chat)
              if (msg.bot_type === 'cta' && msg.bot_data?.cta_type === 'open_chat') {
                return (
                  <div key={idx} className="flex justify-start my-2" data-testid={`bot-cta-${msg.bot_data.chat_type}`}>
                    <div className="max-w-[85%] bg-wa-panel border border-teal-200 rounded-xl shadow-sm p-3">
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className="w-6 h-6 rounded-full bg-teal-600 flex items-center justify-center text-white text-xs font-bold">🤖</div>
                        <span className="text-xs font-semibold text-teal-700">Support Bot</span>
                      </div>
                      <p className="text-sm text-wa-text-primary mb-2">{msg.text}</p>
                      <button
                        onClick={async () => {
                          try {
                            // Post handoff summary to target chat (adds context for handler)
                            await axios.post(`${API}/support/handoff-summary`, {
                              customer_id: user?.id,
                              target_chat_type: msg.bot_data.chat_type,
                            });
                          } catch (e) {
                            console.error('Handoff summary failed', e);
                          }
                          navigate(`/customer-chat?type=${msg.bot_data.chat_type}`);
                        }}
                        className="w-full text-sm px-3 py-2 rounded-lg bg-wa-panel border-l-4 border-[#008069] text-wa-text-primary font-bold hover:from-teal-700 hover:to-emerald-700 shadow"
                        data-testid={`open-chat-${msg.bot_data.chat_type}`}
                      >
                        {msg.bot_data.label || `Open ${msg.bot_data.chat_type} chat`}
                      </button>
                      <div className="text-[10px] text-wa-text-secondary mt-1.5">
                        {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                      </div>
                    </div>
                  </div>
                );
              }

              // Handoff summary — HIDDEN from customer (only admins/handlers should see this)
              if (msg.bot_type === 'handoff_summary') {
                return null;
              }

              return (
                <div key={idx} className="flex justify-start my-2" data-testid={`bot-msg-${msg.bot_type || 'text'}`}>
                  <div className="max-w-[85%] bg-wa-panel border border-teal-200 rounded-xl shadow-sm p-3">
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className="w-6 h-6 rounded-full bg-teal-600 flex items-center justify-center text-white text-xs font-bold">🤖</div>
                      <span className="text-xs font-semibold text-teal-700">Support Bot</span>
                    </div>
                    <p className="text-sm text-wa-text-primary whitespace-pre-line">{msg.text}</p>

                    {/* Suggested hint */}
                    {suggestedKey && msg.bot_type === 'options' && (
                      <div className="mt-2 mb-1 text-[11px] text-wa-text-primary bg-wa-hover border border-amber-200 rounded-md px-2 py-1 flex items-center gap-1">
                        <span>💡</span>
                        <span>आपके message के हिसाब से highlighted option देखें</span>
                      </div>
                    )}

                    {/* Options list */}
                    {(msg.bot_type === 'options' || msg.bot_type === 'sub_options' || msg.bot_type === 'resolution') && msg.bot_data?.options && (
                      <div className="mt-2 flex flex-col gap-1.5">
                        {msg.bot_data.options.map((opt) => {
                          const isSuggested = opt.key === suggestedKey && msg.bot_type === 'options';
                          return (
                          <button
                            key={opt.key}
                            onClick={async () => {
                              setBotTyping(true);
                              try {
                                if (msg.bot_type === 'resolution') {
                                  await axios.post(`${API}/support/resolution`, {
                                    session_id: msg.bot_session_id,
                                    resolved: opt.key === 'yes',
                                  });
                                } else if (msg.bot_type === 'sub_options') {
                                  await axios.post(`${API}/support/bot/action`, {
                                    session_id: msg.bot_session_id,
                                    option_key: msg.bot_data.parent_option || 'talk_person',
                                    sub_option: opt.key,
                                  });
                                } else {
                                  await axios.post(`${API}/support/bot/action`, {
                                    session_id: msg.bot_session_id,
                                    option_key: opt.key,
                                  });
                                }
                                // Instant refresh (fallback if socket is slow / disconnected)
                                if (roomId) {
                                  await loadMessagesRef.current(roomId);
                                }
                              } catch (e) {
                                console.error('Bot action failed', e);
                              } finally {
                                setTimeout(() => setBotTyping(false), 500);
                              }
                            }}
                            className={`text-left text-sm px-3 py-2 rounded-lg border font-medium transition-all ${
                              isSuggested
                                ? 'border-amber-400 bg-wa-hover hover:bg-wa-hover text-wa-text-primary ring-2 ring-amber-300 shadow-md animate-pulse'
                                : 'border-teal-200 bg-teal-50 hover:bg-teal-100 text-teal-800'
                            }`}
                            data-testid={`bot-opt-${opt.key}`}
                          >
                            {isSuggested && '⭐ '}
                            {opt.label}
                          </button>
                          );
                        })}
                      </div>
                    )}
                    <div className="text-[10px] text-wa-text-secondary mt-1.5">
                      {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                    </div>
                  </div>
                </div>
              );
            }

            // Skip internal staff summary cards (SUCCESS / CANCELLED)
            if (msg.text && (msg.text.startsWith('SUCCESS\nUser Name:') || msg.text.startsWith('CANCELLED\nUser Name:'))) {
              return null;
            }

            // Skip if part of image group but not first
            if (isPartOfImageGroup(messages, idx)) return null;
            const imageGroup = getImageGroup(messages, idx);
            
            // Check if this is the first unread INCOMING message (not own message)
            const isIncoming = !isOwn;
            const isPreviousIncoming = idx > 0 ? (messages[idx - 1]?.sender_role !== 'customer' && messages[idx - 1]?.sender_id !== user?.id) : false;
            const isFirstUnread = isIncoming && !msg.read && (idx === 0 || messages[idx - 1]?.read !== false || !isPreviousIncoming);
            const unreadCount = messages.filter(m => m.sender_role !== 'customer' && m.sender_id !== user?.id && !m.read).length;
            
            return (
              <React.Fragment key={idx}>
                {/* Date Separator - WhatsApp Style */}
                {shouldShowDateSeparator(messages, idx) && (
                  <div className="flex items-center justify-center my-3">
                    <div className="bg-white/90 text-wa-icon text-[11px] font-medium px-3 py-1 rounded-lg shadow-sm" data-testid="date-separator">
                      {getDateLabel(msg.timestamp)}
                    </div>
                  </div>
                )}

                {/* Unread Messages Divider - Only for incoming messages */}
                {isFirstUnread && unreadCount > 0 && (
                  <div className="flex items-center justify-center my-4">
                    <div className="bg-wa-hover text-wa-text-primary text-xs font-semibold px-3 py-1 rounded-full shadow-sm">
                      {unreadCount} Unread Message{unreadCount > 1 ? 's' : ''}
                    </div>
                  </div>
                )}
                
              <div id={`msg-${msg.id}`} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                <div
                  // Tail lives on this wrapper, not the bubble: the bubble is
                  // overflow-hidden (to clip media corners) and would cut it off.
                  className={`relative max-w-[85%] group ${isStickerMessage(msg) ? '' : isOwn ? 'wa-tail-out' : 'wa-tail-in'}`}
                  onMouseEnter={() => setHoveredMsg(msg.id)}
                  onMouseLeave={() => setHoveredMsg(null)}
                >
                  {/* Arrow for ALL messages - received messages on right, own messages on right too */}
                  {!(msg.text && msg.text.includes("WITHDRAWAL REQUEST")) && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        const bubbleEl = e.target.closest('.group');
                        const bubbleRect = bubbleEl ? bubbleEl.getBoundingClientRect() : null;
                        if (isOwn && bubbleRect) {
                          // Own messages: menu RIGHT edge at bubble LEFT edge
                          setLongPressMenu({
                            messageId: msg.id,
                            x: bubbleRect.left,
                            y: bubbleRect.top + 10,
                            isOwn: true
                          });
                        } else {
                          setLongPressMenu({
                            messageId: msg.id,
                            x: e.clientX,
                            y: e.clientY || 100,
                            isOwn: false
                          });
                        }
                      }}
                      className={`absolute top-0.5 right-0.5 z-30 w-6 h-6 flex items-center justify-center rounded-full ${isOwn ? 'bg-wa-bubble-out hover:brightness-95' : 'bg-wa-bubble-in hover:brightness-95'} shadow-sm cursor-pointer transition-opacity opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus:opacity-100 focus:pointer-events-auto`}
                      style={{ opacity: longPressMenu?.messageId === msg.id ? 1 : undefined }}
                      data-testid="msg-dropdown-arrow"
                    >
                      <CaretDown size={13} className="text-wa-text-secondary" weight="bold" />
                    </button>
                  )}
                  <div 
                    className={isStickerMessage(msg) ? 'relative w-fit bg-transparent' : `relative pl-2 pr-7 pt-1.5 pb-[2px] rounded-lg shadow-sm overflow-hidden text-wa-text-primary ${isOwn ? 'bg-wa-bubble-out' : 'bg-wa-bubble-in'}`}
                    onContextMenu={(e) => { e.preventDefault(); handleTouchStart(msg.id, e); }}
                    onTouchStart={(e) => handleTouchStart(msg.id, e)}
                    onTouchEnd={handleTouchEnd}
                    onTouchMove={handleTouchMove}
                    onMouseDown={(e) => handleTouchStart(msg.id, { target: e.target, touches: [{ clientX: e.clientX, clientY: e.clientY }] })}
                    onMouseUp={handleTouchEnd}
                    onMouseLeave={handleTouchEnd}
                    onClick={() => handleDoubleTap(msg.id)}
                  >
                    {/* Image Group Grid */}
                    {imageGroup && imageGroup.length > 1 && (
                      <div className="grid grid-cols-2 gap-0.5 rounded-lg overflow-hidden mb-1.5">
                        {imageGroup.slice(0, 4).map((gMsg, gIdx) => {
                          const gUrl = buildMediaUrl(gMsg.image_url);
                          return (
                            <div key={gIdx} className="relative cursor-pointer" onClick={() => handleImageClick(gUrl)}>
                              <ChatImage src={gUrl} alt="" className="w-full h-[120px] object-cover" />
                              {gIdx === 3 && imageGroup.length > 4 && (
                                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                  <span className="text-white text-2xl font-bold">+{imageGroup.length - 4}</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {/* Group caption (last photo's caption shown below grid) */}
                    {imageGroup && imageGroup.length > 1 && (() => {
                      const lastMsg = imageGroup[imageGroup.length - 1];
                      const captionText = lastMsg.caption || (lastMsg.text && lastMsg.text.trim() && lastMsg.text !== '🖼️ Photo' && lastMsg.text !== '🎥 Video' ? lastMsg.text : '');
                      return captionText ? <p className="text-sm mb-1">{captionText}</p> : null;
                    })()}

                    {/* Single Image or Video */}
                    {msg.image_url && isStickerMessage(msg) && (
                      <StickerImage src={buildMediaUrl(msg.image_url)} />
                    )}
                    {msg.image_url && !imageGroup && !isStickerMessage(msg) && (
                      isVideoUrl(msg.image_url) ? (
                        <video
                          src={buildMediaUrl(msg.image_url)}
                          controls
                          className="rounded-lg max-w-[250px] max-h-[300px] mb-1.5"
                          preload="metadata"
                          data-testid="video-message"
                        />
                      ) : (
                        <ChatImage
                          src={buildMediaUrl(msg.image_url)}
                          alt="Shared" 
                          className="rounded-lg max-w-[250px] max-h-[300px] object-cover mb-1.5 cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => handleImageClick(buildMediaUrl(msg.image_url))}
                          onError={(e) => {
                            console.error('Image load error:', msg.image_url);
                          }}
                        />
                      )
                    )}
                    
                    {/* Voice Message */}
                    {msg.voice_url && (
                      <VoiceMessage 
                        audioUrl={(() => {
                          if (msg.voice_url.startsWith('http')) {
                            return msg.voice_url;
                          } else if (msg.voice_url.startsWith('/api/')) {
                            return `${BACKEND_URL}${msg.voice_url}`;
                          } else if (msg.voice_url.startsWith('/uploads/')) {
                            return `${BACKEND_URL}${msg.voice_url}`;
                          } else {
                            return `${API}${msg.voice_url}`;
                          }
                        })()}
                        isSender={msg.sender_id === user?.id}
                      />
                    )}
                    
                    {/* Document Display - Clickable */}
                    {msg.document_url && (
                      <a
                        href={(() => {
                          if (msg.document_url.startsWith('http')) {
                            return msg.document_url;
                          } else if (msg.document_url.startsWith('/api/')) {
                            return `${BACKEND_URL}${msg.document_url}`;
                          } else if (msg.document_url.startsWith('/uploads/')) {
                            return `${BACKEND_URL}${msg.document_url}`;
                          } else {
                            return `${API}${msg.document_url}`;
                          }
                        })()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-3 bg-wa-hover rounded-lg hover:bg-wa-hover transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                          <FileText size={24} className="text-white" weight="fill" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-wa-text-primary truncate">
                            {msg.document_name || 'Document'}
                          </p>
                          <p className="text-xs text-wa-text-secondary">Tap to open</p>
                        </div>
                      </a>
                    )}
                    
                    {/* Reply Reference - WhatsApp Style */}
                    {msg.reply_to && (
                      <div className={`mb-1 pl-2 py-0.5 border-l-2 rounded text-xs ${
                        isOwn ? 'border-green-700 bg-wa-hover/30' : 'border-blue-600 bg-wa-hover/30'
                      }`}>
                        <div className="font-semibold text-[10px] opacity-70">
                          {msg.reply_to.sender_name}
                        </div>
                        <div className="truncate text-[11px] opacity-60">
                          {msg.reply_to.text || '📷 Media'}
                        </div>
                      </div>
                    )}
                    
                    {/* Call Info Bubble - WhatsApp Style */}
                    {msg.call_info ? (
                      <div className="flex items-center gap-2 py-1">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          msg.call_info.status === 'completed' ? 'bg-wa-hover' : 'bg-wa-hover'
                        }`}>
                          <Phone size={16} weight="fill" className={`${
                            msg.call_info.status === 'completed' ? 'text-green-600' : 'text-red-500'
                          } ${msg.call_info.caller_id === user?.id ? 'rotate-[135deg]' : ''}`} />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-wa-text-primary">Voice call</p>
                          <p className={`text-xs ${msg.call_info.status === 'completed' ? 'text-wa-text-secondary' : 'text-red-500'}`}>
                            {msg.call_info.status === 'completed' 
                              ? (msg.call_info.duration >= 60 ? `${Math.floor(msg.call_info.duration/60)} min` : `${msg.call_info.duration} secs`)
                              : 'No answer'}
                          </p>
                        </div>
                      </div>
                    ) : null}
                    
                    {/* Text + WhatsApp-style inline time */}
                    {msg.text && !msg.call_info ? (
                      <div className="leading-[19px]">
                        <span className="text-[14.5px] text-wa-text-primary break-words whitespace-pre-line">
                          {renderMessageText(msg.text)}
                        </span>
                        <span className="float-right ml-[8px] mt-[3px] flex items-center gap-[3px] leading-none">
                          {msg.starred_by?.includes(user?.id) && <Star size={11} weight="fill" className="text-yellow-500" />}
                          {msg.edited && <span className="text-[10.5px] text-wa-text-secondary italic">edited</span>}
                          <span className="text-[11px] text-wa-text-secondary">
                            {new Date(msg.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                          <ReadReceipts message={msg} isSender={isOwn} />
                        </span>
                      </div>
                    ) : (
                      <div className="flex justify-end items-center gap-[3px] mt-[2px] mb-[1px]">
                        {msg.starred_by?.includes(user?.id) && <Star size={11} weight="fill" className="text-yellow-500" />}
                        {msg.edited && <span className="text-[10.5px] text-wa-text-secondary italic">edited</span>}
                        <span className="text-[11px] text-wa-text-secondary leading-none">
                          {new Date(msg.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        <ReadReceipts message={msg} isSender={isOwn} />
                      </div>
                    )}
                  </div>
                  
                  {/* Reactions */}
                  {Object.keys(reactions).length > 0 && (
                    <div className="flex gap-1 mt-1">
                      {Object.entries(reactions).map(([emoji, userIds]) => (
                        <div 
                          key={emoji}
                          className="bg-wa-panel rounded-full px-2 py-1 text-xs shadow-sm border flex items-center gap-1"
                        >
                          <span>{emoji}</span>
                          <span className="text-wa-text-secondary">{userIds.length}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Emoji Picker */}
                  {showEmojiPicker === msg.id && (
                    <div className="absolute bottom-full mb-2 bg-wa-panel rounded-lg shadow-lg p-2 flex gap-2 z-50">
                      {EMOJIS.map(emoji => (
                        <button
                          key={emoji}
                          onClick={() => handleAddReaction(msg.id, emoji)}
                          className="text-2xl hover:scale-125 transition-transform"
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              </React.Fragment>
            );
          })}
          <div ref={messagesEndRef} style={{ overflowAnchor: 'auto', height: '1px' }} />

          {/* Bot Typing Indicator — brief animation after clicking an option */}
          {botTyping && (
            <div className="flex justify-start my-2" data-testid="bot-typing">
              <div className="bg-wa-panel border border-teal-200 rounded-xl shadow-sm px-3 py-2 flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-teal-600 flex items-center justify-center text-white text-[10px]">🤖</div>
                <div className="flex gap-1">
                  <span className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                <span className="text-[11px] text-teal-700">Support Bot typing...</span>
              </div>
            </div>
          )}
          
          {/* Typing Indicator */}
          {handlerTyping && (
            <div className="flex justify-start">
              <div className="bg-wa-panel px-4 py-2 rounded-lg shadow">
                <TypingIndicator username="Handler" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Scroll to Bottom Arrow - WhatsApp Style */}
      {isUserScrolling && (
        <button
          onClick={() => { setIsUserScrolling(false); scrollToBottom(); }}
          className="fixed bottom-24 right-4 z-40 w-10 h-10 bg-wa-panel rounded-full shadow-lg flex items-center justify-center border border-wa-border"
          data-testid="scroll-to-bottom-btn"
        >
          <CaretDown size={20} className="text-wa-icon" weight="bold" />
        </button>
      )}

      {/* Soft nudge (2-4 min) — subtle inline notice */}
      {showSoftNudge && !showTroubleBanner && (
        <div className="fixed bottom-20 left-3 right-3 z-[60] bg-wa-hover border border-sky-200 rounded-lg px-3 py-1.5 flex items-center gap-2 text-xs text-wa-text-primary" data-testid="soft-nudge-banner">
          <span>💬</span>
          <span className="flex-1">Handler को थोड़ा time लग रहा है, please wait...</span>
          <button
            onClick={() => setShowSoftNudge(false)}
            className="text-sky-600 hover:text-wa-text-primary px-1"
          >
            ✕
          </button>
        </div>
      )}

      {/* Having Trouble Banner - deposit/withdrawal only, when handler hasn't replied for 5+ min */}
      {showTroubleBanner && (
        <div className="fixed bottom-24 left-3 right-3 z-[60] bg-wa-panel border-l-4 border-[#d97706] border border-amber-300 rounded-xl shadow-lg px-4 py-3 flex items-center gap-3" data-testid="having-trouble-banner">
          <div className="text-2xl">⏱️</div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-wa-text-primary">Having Trouble?</div>
            <div className="text-xs text-wa-text-primary">बहुत देर हो गई reply आने में। Support team से बात करें?</div>
          </div>
          <button
            onClick={handleEscalateToSupport}
            disabled={escalating}
            className="shrink-0 px-3 py-1.5 rounded-full bg-teal-600 text-white text-xs font-bold hover:bg-teal-700 disabled:opacity-50"
            data-testid="escalate-support-btn"
          >
            {escalating ? '...' : 'Move to Support'}
          </button>
          <button
            onClick={() => setShowTroubleBanner(false)}
            className="shrink-0 text-wa-text-primary hover:text-wa-text-primary text-xs px-2"
            data-testid="dismiss-trouble-banner"
          >
            ✕
          </button>
        </div>
      )}

      {/* Input Area - WhatsApp Style Input */}
      <div ref={inputContainerRef} className="absolute bottom-0 left-0 right-0 z-50">
        {/* Quick Reply Suggestions */}
        {chatType !== 'deposit' && chatType !== 'withdrawal' ? (
          showQuickReplies && (
            <div className="bg-wa-panel border-t border-wa-border px-3 py-2">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-wa-text-secondary">Quick Replies</span>
                <button
                  onClick={() => setShowQuickReplies(false)}
                  aria-label="Hide quick replies"
                  className="-mr-1 flex h-9 w-9 items-center justify-center rounded-full hover:bg-wa-hover"
                >
                  <X size={16} className="text-wa-text-secondary" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {chatType === 'support' && (
                  <>
                    <button
                      onClick={() => handleWhatsAppSendMessage('Hi, I want Support')}
                      className="px-3 py-1.5 bg-[#e8f5e9] text-[#128C7E] text-sm rounded-full border border-[#25D366] hover:bg-[#d4edda] transition-colors"
                    >
                      Hi, I want Support
                    </button>
                  </>
                )}
                {chatType === 'inquiry' && (
                  <>
                    <button
                      onClick={() => handleWhatsAppSendMessage('Hi, I want ID.')}
                      className="px-3 py-1.5 bg-[#e8f5e9] text-[#128C7E] text-sm rounded-full border border-[#25D366] hover:bg-[#d4edda] transition-colors"
                    >
                      Hi, I want ID
                    </button>
                  </>
                )}
              </div>
            </div>
          )
        ) : chatType === 'deposit' ? (
          depositFlowState === 'idle' && showDepositQuickReply && (
            <div className="bg-wa-panel border-t border-wa-border px-3 py-2">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-wa-text-secondary font-semibold">Quick Replies</span>
                <button
                  onClick={() => setShowDepositQuickReply(false)}
                  aria-label="Hide quick replies"
                  className="-mr-1 flex h-9 w-9 items-center justify-center rounded-full hover:bg-wa-hover"
                >
                  <X size={16} className="text-wa-text-secondary" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={handleDepositRequestClick}
                  className="px-3 py-1.5 bg-[#e8f5e9] text-[#128C7E] text-sm font-semibold rounded-full border border-[#25D366] hover:bg-[#d4edda] transition-colors"
                >
                  Hi, I want Deposit
                </button>
              </div>
            </div>
          )
        ) : (
          /* Withdrawal quick reply */
          showWithdrawQuickReply && (
            <div className="bg-wa-panel border-t border-wa-border px-3 py-2">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-wa-text-secondary font-semibold">Quick Replies</span>
                <button
                  onClick={() => setShowWithdrawQuickReply(false)}
                  aria-label="Hide quick replies"
                  className="-mr-1 flex h-9 w-9 items-center justify-center rounded-full hover:bg-wa-hover cursor-pointer"
                >
                  <X size={16} className="text-wa-text-secondary" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => {
                    handleWhatsAppSendMessage('Hi, I want Withdrawal, Please send Withdrawal Form.');
                    setShowWithdrawQuickReply(false);
                  }}
                  className="px-3 py-1.5 bg-[#e8f5e9] text-[#128C7E] text-sm font-semibold rounded-full border border-[#25D366] hover:bg-[#d4edda] transition-colors cursor-pointer"
                >
                  Hi, I Want Withdrawal
                </button>
              </div>
            </div>
          )
        )}
        
        <WhatsAppInput
          onSendMessage={handleWhatsAppSendMessage}
          onSendMedia={handleMediaSelected}
          onOpenMediaPicker={() => setShowMediaPicker(true)}
          onTyping={handleTypingIndicator}
          placeholder="Type a message..."
          disabled={(chatType === 'deposit' && depositFlowState === 'options_pending') || (chatType === 'withdrawal' && showWithdrawModal)}
          replyingTo={replyingTo}
          onCancelReply={cancelReply}
          userId={user?.id}
          userRole="customer"
        />
      </div>

      {/* Deposit Options Centered Popup Modal */}
      {chatType === 'deposit' && depositFlowState === 'options_pending' && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4 backdrop-blur-[2px]">
          <div className="bg-wa-panel rounded-xl shadow-2xl max-w-sm w-full border border-wa-border overflow-hidden animate-fade-in animate-duration-200 text-wa-text-primary">
            {/* Header */}
            <div className="bg-blue-600 px-4 py-3 flex items-center justify-between text-white font-semibold shadow-sm">
              <div className="flex items-center gap-2">
                <span className="text-lg">💰</span>
                <span>Deposit Request</span>
              </div>
              <button 
                onClick={() => {
                  updateDepositFlowState('idle');
                  setCustomAmount('');
                }}
                className="p-1 hover:bg-white/10 rounded-full transition-colors text-white"
                aria-label="Close"
              >
                <X size={18} weight="bold" />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-wa-text-secondary mb-1">Amount (₹)</label>
                <input
                  type="number"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  placeholder="e.g. 2000"
                  className="w-full px-3 py-2 border border-wa-border rounded-lg bg-wa-hover text-wa-text-primary text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <p className="text-[11px] text-wa-text-secondary">
                Enter an amount OR tap a quick chip above to send.
              </p>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    updateDepositFlowState('idle');
                    setCustomAmount('');
                  }}
                  className="px-4 py-2 border border-wa-border bg-wa-panel rounded-lg text-wa-text-primary hover:bg-wa-hover font-semibold text-sm transition-all"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSendDepositRequest}
                  disabled={isSubmittingDeposit}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg font-bold text-sm transition-all shadow-md"
                >
                  {isSubmittingDeposit ? 'Sending...' : 'Send Request'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Withdrawal Form Centered Popup Modal */}
      {chatType === 'withdrawal' && showWithdrawModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4 backdrop-blur-[2px] overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full border border-gray-200 overflow-hidden animate-fade-in text-gray-800 my-auto relative">
            {/* Header */}
            <div className="px-5 py-4 flex items-center justify-between border-b border-gray-100">
              <div className="flex items-center gap-2 font-bold text-gray-900 text-base">
                <span className="text-xl">🏦</span>
                <span>Withdraw Request</span>
              </div>
              <button 
                onClick={() => setShowWithdrawModal(false)}
                className="p-1.5 hover:bg-gray-100 rounded-full transition-colors text-gray-500 hover:text-gray-800"
                aria-label="Close"
              >
                <X size={18} weight="bold" />
              </button>
            </div>

            {/* Body */}
            <form onSubmit={handleWithdrawalSubmit} className="p-5 space-y-4">
              {/* Amount */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Amount (₹)
                </label>
                <input
                  type="number"
                  required
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  className="w-full px-3.5 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00a884] text-sm text-gray-800 placeholder-gray-400"
                  placeholder="e.g. 2000"
                />
              </div>

              {/* Bank Details section */}
              {savedBankDetails && (savedBankDetails.bank_account_number || savedBankDetails.upi_id) ? (
                /* Scenario A: Saved Bank Details (Img 2) */
                <div className="bg-[#eefcf7] border border-[#a3e6cd] rounded-xl p-3.5 space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 font-bold text-[#056049]">
                      <span>💳</span>
                      <span>Primary Account (saved · auto-used)</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowAccountLockedPopup(true)}
                      className="px-2.5 py-1 text-[11px] font-semibold bg-white border border-[#056049]/30 text-[#056049] rounded-md hover:bg-white/80 transition-colors shadow-xs"
                    >
                      Change
                    </button>
                  </div>
                  
                  <div className="text-gray-700 space-y-0.5 pt-1">
                    {savedBankDetails.bank_account_number ? (
                      <>
                        <div className="font-semibold text-gray-900">
                          {savedBankDetails.bank_name || 'Bank'} · A/c {savedBankDetails.bank_account_number}
                        </div>
                        {savedBankDetails.account_holder_name && (
                          <div className="flex items-center gap-1 text-gray-600">
                            <span>👤</span>
                            <span>{savedBankDetails.account_holder_name}</span>
                          </div>
                        )}
                        {savedBankDetails.ifsc_code && (
                          <div className="text-gray-500 font-mono text-[11px]">
                            IFSC {savedBankDetails.ifsc_code}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="font-semibold text-gray-900">
                        UPI ID: {savedBankDetails.upi_id}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                /* Scenario B: Add your withdraw account (Img 3) */
                <div className="border border-amber-200 bg-amber-50/40 rounded-xl p-3.5 space-y-3">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-amber-900">
                    <span>💳</span>
                    <span>Add your withdraw account (saved for future)</span>
                  </div>

                  <div className="space-y-2 text-xs">
                    <input
                      type="text"
                      value={withdrawHolderName}
                      onChange={(e) => setWithdrawHolderName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00a884] text-xs"
                      placeholder="A/c Holder Name"
                    />

                    <input
                      type="text"
                      value={withdrawAccountNumber}
                      onChange={(e) => setWithdrawAccountNumber(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00a884] text-xs"
                      placeholder="Bank Account Number"
                    />

                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={withdrawIfsc}
                        onChange={(e) => setWithdrawIfsc(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00a884] text-xs"
                        placeholder="IFSC"
                      />
                      <input
                        type="text"
                        value={withdrawBankName}
                        onChange={(e) => setWithdrawBankName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00a884] text-xs"
                        placeholder="Bank Name"
                      />
                    </div>

                    <div className="text-center text-[11px] text-gray-400 font-medium py-0.5">
                      — or —
                    </div>

                    <input
                      type="text"
                      value={withdrawUpi}
                      onChange={(e) => setWithdrawUpi(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00a884] text-xs"
                      placeholder="UPI ID (e.g. name@ybl)"
                    />
                  </div>

                  <p className="text-[10px] text-amber-800 font-medium leading-tight">
                    (Optional) Fill Account + IFSC OR UPI ID — we'll save it to your profile so future withdrawals auto-fill.
                  </p>
                </div>
              )}

              {/* Helper text */}
              <p className="text-[11px] text-gray-500 text-center">
                Enter an amount OR tap a quick chip above to send.
              </p>

              {/* Action Buttons */}
              <div className="flex gap-2.5 pt-1">
                <button
                  type="button"
                  onClick={() => setShowWithdrawModal(false)}
                  className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-lg text-xs transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingWithdrawal}
                  className="flex-1 py-2 bg-[#00a884] hover:bg-[#008f70] text-white font-semibold rounded-lg text-xs transition-colors shadow-xs disabled:opacity-50"
                >
                  {isSubmittingWithdrawal ? 'Sending...' : 'Send Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Account Details Locked Popup (Img 4) */}
      {showAccountLockedPopup && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[110] p-4 backdrop-blur-[2px]">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full border border-gray-200 overflow-hidden animate-fade-in text-gray-800 p-6 relative">
            <button 
              onClick={() => setShowAccountLockedPopup(false)}
              className="absolute top-4 right-4 p-1 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600"
            >
              <X size={18} weight="bold" />
            </button>

            <div className="flex flex-col items-center text-center space-y-3">
              {/* Lock Icon */}
              <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 text-2xl shadow-inner">
                🔒
              </div>

              {/* Title */}
              <h3 className="text-lg font-bold text-gray-900">
                Account details locked
              </h3>

              {/* Description */}
              <p className="text-xs text-gray-600 leading-relaxed">
                Account details yahaan se change nahi ho paayenge.<br />
                Change karvaane ke liye <strong className="text-gray-900">Support section</strong> mein jaayein aur wahaan account change ki request raise karein.
              </p>

              {/* Action Buttons */}
              <div className="flex gap-3 w-full pt-3">
                <button
                  type="button"
                  onClick={() => setShowAccountLockedPopup(false)}
                  className="flex-1 py-2.5 border border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold rounded-xl text-xs transition-colors"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAccountLockedPopup(false);
                    setShowWithdrawModal(false);
                    navigate('/chat?type=support');
                  }}
                  className="flex-1 py-2.5 bg-[#8b5cf6] hover:bg-[#7c3aed] text-white font-semibold rounded-xl text-xs transition-colors flex items-center justify-center gap-1.5 shadow-sm"
                >
                  <span>📞</span>
                  <span>Go to Support</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Long Press Menu — portalled so it cannot be clipped by the chat panel. */}
      {(() => {
        if (!longPressMenu) return null;
        const menuMsg = messages.find(m => m.id === longPressMenu.messageId);
        const menuIsOwn = longPressMenu.isOwn || menuMsg?.sender_id === user?.id;
        const isStarred = menuMsg?.starred_by?.includes(user?.id);
        const isPinned = pinnedMessage?.id === longPressMenu.messageId;

        return (
          <MessageActionMenu
            open
            x={longPressMenu.x}
            y={longPressMenu.y}
            isOwn={menuIsOwn}
            onClose={() => setLongPressMenu(null)}
          >
            <MessageActionItem
              icon={<Copy size={20} />}
              label="Copy"
              onClick={() => { if (menuMsg) handleCopyMessage(menuMsg.text); }}
            />

            <MessageActionItem
              icon={<ArrowBendUpLeft size={20} />}
              label="Reply"
              onClick={() => { if (menuMsg) handleReplyMessage(menuMsg); }}
            />

            <MessageActionItem
              icon={<Star size={20} weight={isStarred ? 'fill' : 'regular'} className={isStarred ? 'text-yellow-500' : ''} />}
              label={isStarred ? 'Unstar' : 'Star'}
              onClick={async () => {
                try {
                  const res = await axios.post(`${API}/chat/message/${longPressMenu.messageId}/star?user_id=${user.id}`);
                  setMessages(prev => prev.map(m => {
                    if (m.id === longPressMenu.messageId) {
                      const starred = m.starred_by || [];
                      return res.data.starred
                        ? { ...m, starred_by: [...starred, user.id] }
                        : { ...m, starred_by: starred.filter(id => id !== user.id) };
                    }
                    return m;
                  }));
                  toast(res.data.starred ? 'Message starred' : 'Star removed', { duration: 1500 });
                } catch (err) { toast.error('Failed to star message'); }
                setLongPressMenu(null);
              }}
            />

            <MessageActionItem
              icon={<PushPin size={20} />}
              label={isPinned ? 'Unpin' : 'Pin'}
              testId="pin-message-btn"
              onClick={async () => {
                try {
                  if (isPinned) {
                    await axios.delete(`${API}/chat/room/${roomId}/pin`);
                    setPinnedMessage(null);
                    toast('Message unpinned', { duration: 1500 });
                  } else {
                    await axios.post(`${API}/chat/room/${roomId}/pin`, { message_id: longPressMenu.messageId, user_id: user.id });
                    setPinnedMessage(menuMsg);
                    toast('Message pinned', { duration: 1500 });
                  }
                } catch (err) { toast.error('Failed to pin message'); }
                setLongPressMenu(null);
              }}
            />

            {menuMsg && menuMsg.sender_id === user?.id && menuMsg.text ? (
              <MessageActionItem
                icon={<PencilSimple size={20} />}
                label="Edit"
                testId="edit-message-btn"
                onClick={() => {
                  setEditingMsg(menuMsg);
                  setEditText(menuMsg.text || '');
                  setLongPressMenu(null);
                }}
              />
            ) : null}

            <MessageActionItem
              icon={<Trash size={20} />}
              label="Delete"
              destructive
              onClick={() => handleDeleteClick(longPressMenu.messageId)}
            />
          </MessageActionMenu>
        );
      })()}

      {/* Delete Modal - Simple "Delete" for Customer */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-wa-panel rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Delete Message</h3>
            <p className="text-wa-text-secondary mb-6">This will delete the message for you only.</p>
            <div className="space-y-2">
              <button
                disabled={isDeletingMessage}
                onClick={handleDeleteMessage}
                className="w-full px-4 py-3 bg-wa-hover hover:bg-red-200 rounded-lg text-red-600 font-semibold flex items-center justify-between cursor-pointer disabled:opacity-50"
              >
                <span>Delete</span>
                {isDeletingMessage && (
                  <span className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin inline-block" />
                )}
              </button>
              <button
                disabled={isDeletingMessage}
                onClick={() => {
                  setShowDeleteModal(false);
                  setMessageToDelete(null);
                }}
                className="w-full px-4 py-3 border rounded-lg hover:bg-wa-hover cursor-pointer disabled:opacity-50 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inquiry/Support leave confirmation. Both actions intentionally keep
          the active chat open, as requested. */}
      {showLeaveChatModal && isProtectedCustomerChat && !isEmbedded && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-[2px]">
          <div className="w-full max-w-sm rounded-3xl bg-white p-6 text-center shadow-2xl">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100 text-red-500">
              <ArrowLeft size={28} weight="bold" />
            </div>
            <h3 className="text-2xl font-extrabold text-slate-900">Leave chat?</h3>
            <p className="mt-2 text-sm font-semibold text-slate-500">Your conversation will stay here.</p>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setShowLeaveChatModal(false)}
                className="rounded-xl bg-slate-100 px-4 py-3 font-bold text-slate-900 transition hover:bg-slate-200"
              >
                Stay
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowLeaveChatModal(false);
                  if (user && user.role === 'customer' && !user.is_guest && !user.is_inquiry) {
                    navigate('/customer-select', { replace: true });
                  } else {
                    navigate('/customer-login', { replace: true });
                  }
                }}
                className="rounded-xl bg-red-500 px-4 py-3 font-bold text-white shadow-lg shadow-red-200 transition hover:bg-red-600"
              >
                Leave
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Support Menu Modal */}
      {showSupportMenu && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-wa-panel rounded-lg p-6 max-w-sm w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Support Options</h3>
              <button
                onClick={() => setShowSupportMenu(false)}
                className="text-wa-text-secondary hover:text-wa-text-primary"
              >
                <X size={24} weight="bold" />
              </button>
            </div>
            
            {(() => {
              const phoneNumber = supportInfo?.support_phone || supportInfo?.phone;
              const whatsappUrl = (supportInfo?.whatsapp_link && supportInfo?.whatsapp_link !== '#') ? supportInfo.whatsapp_link : ((supportInfo?.whatsapp && supportInfo?.whatsapp !== '#') ? supportInfo.whatsapp : null);
              const supportUrl = (supportInfo?.support_chat_link && supportInfo?.support_chat_link !== '#') ? supportInfo.support_chat_link : ((supportInfo?.support_link && supportInfo?.support_link !== '#') ? supportInfo.support_link : null);
              const telegramUrl = (supportInfo?.telegram_link && supportInfo?.telegram_link !== '#') ? supportInfo.telegram_link : null;
              const instagramUrl = (supportInfo?.instagram_link && supportInfo?.instagram_link !== '#') ? supportInfo.instagram_link : null;

              const hasAnyOption = phoneNumber || whatsappUrl || supportUrl || telegramUrl || instagramUrl || !user?.is_inquiry;

              return (
                <div className="space-y-3">
                  {/* Chat Support - Hide for inquiry guests */}
                  {!user?.is_inquiry && (
                    <button
                      onClick={() => {
                        setShowSupportMenu(false);
                        // Navigate to support chat
                        navigate('/customer-chat?type=support');
                      }}
                      className="w-full px-4 py-3 bg-wa-hover hover:bg-wa-hover rounded-lg flex items-center gap-3 transition-colors border border-purple-200"
                    >
                      <svg className="w-6 h-6 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 5a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm3.293 1.293a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 01-1.414-1.414L7.586 10 5.293 7.707a1 1 0 010-1.414z" clipRule="evenodd" fillRule="evenodd"></path>
                      </svg>
                      <div className="text-left flex-1">
                        <p className="font-semibold text-wa-text-primary">Chat Support</p>
                        <p className="text-sm text-wa-text-secondary">Message us directly</p>
                      </div>
                    </button>
                  )}

                  {/* Call Support */}
                  {phoneNumber && (
                    <button
                      onClick={() => {
                        window.location.href = `tel:${phoneNumber}`;
                        setShowSupportMenu(false);
                      }}
                      className="w-full px-4 py-3 bg-wa-hover hover:bg-wa-hover rounded-lg flex items-center gap-3 transition-colors border border-blue-200"
                    >
                      <Phone size={24} weight="fill" className="text-blue-600 flex-shrink-0" />
                      <div className="text-left flex-1 min-w-0 overflow-hidden">
                        <p className="font-semibold text-wa-text-primary">Call Support</p>
                        <p className="text-sm text-wa-text-secondary truncate">{phoneNumber}</p>
                      </div>
                    </button>
                  )}

                  {/* WhatsApp Support */}
                  {whatsappUrl && (
                    <button
                      onClick={() => {
                        const url = whatsappUrl.startsWith('http') ? whatsappUrl : `https://wa.me/${whatsappUrl.replace(/[^0-9+]/g, '')}?text=${encodeURIComponent('Hello, I need support')}`;
                        window.open(url, '_blank');
                        setShowSupportMenu(false);
                      }}
                      className="w-full px-4 py-3 bg-wa-hover hover:bg-wa-hover rounded-lg flex items-center gap-3 transition-colors border border-green-200"
                    >
                      <WhatsappLogo size={24} weight="fill" className="text-green-600 flex-shrink-0" />
                      <div className="text-left flex-1 min-w-0 overflow-hidden">
                        <p className="font-semibold text-wa-text-primary">WhatsApp Support</p>
                        <p className="text-sm text-wa-text-secondary truncate">{whatsappUrl}</p>
                      </div>
                    </button>
                  )}

                  {/* Telegram Support */}
                  {telegramUrl && (
                    <button
                      onClick={() => {
                        window.open(telegramUrl, '_blank');
                        setShowSupportMenu(false);
                      }}
                      className="w-full px-4 py-3 bg-wa-hover hover:bg-wa-hover rounded-lg flex items-center gap-3 transition-colors border border-sky-200"
                    >
                      <TelegramLogo size={24} weight="fill" className="text-sky-500 flex-shrink-0" />
                      <div className="text-left flex-1 min-w-0 overflow-hidden">
                        <p className="font-semibold text-wa-text-primary">Telegram Support</p>
                        <p className="text-sm text-wa-text-secondary truncate">{telegramUrl}</p>
                      </div>
                    </button>
                  )}

                  {/* Support Link */}
                  {supportUrl && (
                    <button
                      onClick={() => {
                        window.open(supportUrl, '_blank');
                        setShowSupportMenu(false);
                      }}
                      className="w-full px-4 py-3 bg-wa-hover hover:bg-wa-hover rounded-lg flex items-center gap-3 transition-colors border border-purple-200"
                    >
                      <Link size={24} weight="bold" className="text-purple-600 flex-shrink-0" />
                      <div className="text-left flex-1 min-w-0 overflow-hidden">
                        <p className="font-semibold text-wa-text-primary">Support Link</p>
                        <p className="text-sm text-wa-text-secondary truncate">{supportUrl}</p>
                      </div>
                    </button>
                  )}

                  {/* No support options configured */}
                  {!hasAnyOption && (
                    <div className="text-center py-6 text-wa-text-secondary">
                      <p>No support options configured yet.</p>
                      <p className="text-sm mt-2">Please contact admin.</p>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Status Viewer */}
      {showStatusViewer && (
        <StatusViewer
          customerId={user.id}
          onClose={() => setShowStatusViewer(false)}
        />
      )}

      {/* Media Picker */}
      {showMediaPicker && (
        <MediaPicker
          onMediaSelected={handleMediaSelected}
          onClose={() => setShowMediaPicker(false)}
          userRole="customer"
        />
      )}

      {/* Media Viewer - WhatsApp Style */}
      {showImagePreview && previewImageUrl && (
        <div className="fixed inset-0 bg-black z-50 flex flex-col" data-testid="media-viewer">
          <div className="flex items-center justify-between p-4 text-white">
            <button onClick={closeImagePreview} className="p-2 hover:bg-white/10 rounded-full">
              <X size={24} weight="bold" />
            </button>
            <span className="text-sm font-medium">
              {allMediaUrls.length > 1 ? `${currentMediaIdx + 1} / ${allMediaUrls.length}` : 'Photo'}
            </span>
            <div className="w-10" />
          </div>
          <div className="flex-1 flex items-center justify-center px-4 relative">
            {allMediaUrls.length > 1 && currentMediaIdx > 0 && (
              <button onClick={() => { const i = currentMediaIdx - 1; setCurrentMediaIdx(i); setPreviewImageUrl(allMediaUrls[i]); }}
                className="absolute left-2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white z-10">
                <ArrowLeft size={20} weight="bold" />
              </button>
            )}
            <img src={previewImageUrl} alt="Preview" className="max-w-full max-h-[70vh] object-contain" />
            {/* Per-photo caption */}
            {allMediaCaptions[currentMediaIdx] && (
              <div className="absolute bottom-0 left-0 right-0 bg-black/60 backdrop-blur-sm px-4 py-2">
                <p className="text-white text-sm text-center">{allMediaCaptions[currentMediaIdx]}</p>
              </div>
            )}
            {allMediaUrls.length > 1 && currentMediaIdx < allMediaUrls.length - 1 && (
              <button onClick={() => { const i = currentMediaIdx + 1; setCurrentMediaIdx(i); setPreviewImageUrl(allMediaUrls[i]); }}
                className="absolute right-2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white z-10">
                <CaretDown size={20} weight="bold" className="rotate-[-90deg]" />
              </button>
            )}
          </div>
          <div className="flex items-center justify-around p-4 border-t border-white/10">
            <button onClick={() => { if(navigator.share) navigator.share({url: previewImageUrl}).catch(()=>{}); else { navigator.clipboard.writeText(previewImageUrl); toast('Link copied'); } }}
              className="flex flex-col items-center gap-1 text-white/70 hover:text-white">
              <ShareFat size={22} /><span className="text-[10px]">Forward</span>
            </button>
            <button onClick={closeImagePreview} className="flex flex-col items-center gap-1 text-white/70 hover:text-white">
              <ArrowLeft size={22} /><span className="text-[10px]">Back</span>
            </button>
            <a href={previewImageUrl} download="photo.jpg" target="_blank" rel="noopener noreferrer"
              className="flex flex-col items-center gap-1 text-white/70 hover:text-white">
              <CaretDown size={22} weight="bold" /><span className="text-[10px]">Download</span>
            </a>
          </div>
        </div>
      )}

      {/* Edit Message Modal */}
      {editingMsg && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-wa-panel rounded-xl max-w-sm w-full p-5 shadow-2xl" data-testid="edit-message-modal">
            <h3 className="text-lg font-bold text-wa-text-primary mb-3">Edit Message</h3>
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              className="w-full px-3 py-2 border border-wa-border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm min-h-[80px] resize-none"
              autoFocus
              disabled={isSavingEdit}
            />
            <div className="flex gap-3 mt-4">
              <button
                disabled={isSavingEdit}
                onClick={() => { setEditingMsg(null); setEditText(''); setIsSavingEdit(false); }}
                className="flex-1 px-4 py-2.5 border border-wa-border rounded-lg text-wa-text-primary hover:bg-wa-hover disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                disabled={isSavingEdit}
                onClick={async () => {
                  if (!editText.trim()) { toast.error('Message cannot be empty'); return; }
                  setIsSavingEdit(true);
                  try {
                    await axios.put(`${API}/chat/message/${editingMsg.id}/edit`, {
                      user_id: user.id,
                      text: editText.trim()
                    });
                    setMessages(prev => prev.map(m => m.id === editingMsg.id ? { ...m, text: editText.trim(), edited: true } : m));
                    toast.success('Message edited');
                    setEditingMsg(null);
                    setEditText('');
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Failed to edit');
                  } finally {
                    setIsSavingEdit(false);
                  }
                }}
                className="flex-1 px-4 py-2.5 bg-[#00a884] text-white rounded-lg hover:bg-[#008f72] disabled:opacity-75 flex items-center justify-center gap-2 font-medium"
              >
                {isSavingEdit ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <span>Save</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Audio Call Screen */}
      {activeCall && (
        <AudioCallScreen
          socket={socket}
          callId={activeCall.callId}
          callerId={activeCall.callerId}
          calleeId={activeCall.calleeId}
          callerName={activeCall.callerName}
          calleeName={activeCall.calleeName}
          callerProfilePic={activeCall.callerProfilePic}
          calleeProfilePic={activeCall.calleeProfilePic}
          isIncoming={activeCall.isIncoming}
          currentUserId={user?.id}
          currentUserRole="customer"
          isConference={activeCall.isConference || false}
          onClose={() => setActiveCall(null)}
          API={API}
        />
      )}

      {/* Incoming Call */}
      {incomingCall && !activeCall && (
        <div className="fixed inset-0 z-[90] bg-black/80 flex items-center justify-center p-4">
          <div className="bg-[#1a3a2a] rounded-2xl p-8 max-w-xs w-full text-center">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#00a884] to-[#008069] mx-auto mb-4 flex items-center justify-center">
              <span className="text-3xl font-bold text-white">{(incomingCall.callerName || 'U')[0]?.toUpperCase()}</span>
            </div>
            <h3 className="text-xl font-bold text-white mb-1">{incomingCall.callerName}</h3>
            <p className="text-white/50 text-sm mb-6">
              {incomingCall.isConference 
                ? `Conference call (${incomingCall.participantNames?.join(', ')})` 
                : 'Incoming audio call...'}
            </p>
            <div className="flex justify-around">
              <button onClick={() => { socket.emit('call_reject', { call_id: incomingCall.callId }); setIncomingCall(null); }}
                className="w-14 h-14 rounded-full bg-red-500 flex items-center justify-center">
                <Phone size={24} weight="fill" className="text-white rotate-[135deg]" />
              </button>
              <button onClick={() => {
                setActiveCall({ callId: incomingCall.callId, callerId: incomingCall.callerId, calleeId: user?.id,
                  callerName: incomingCall.callerName, calleeName: user?.username, isIncoming: true,
                  isConference: incomingCall.isConference || false });
                setIncomingCall(null);
              }} className="w-14 h-14 rounded-full bg-[#00a884] flex items-center justify-center">
                <Phone size={24} weight="fill" className="text-white" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Support Request Modal */}
      {showSupportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <span className="p-1 text-white bg-[#e11d48] rounded font-bold text-xs">SOS</span>
                <h3 className="font-bold text-gray-800 text-base">Support Request</h3>
              </div>
              <button 
                onClick={() => setShowSupportModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
              >
                ✕
              </button>
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              const query = e.target.queryText.value;
              handleSupportSubmit(query);
            }} className="p-5">
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-500 mb-1.5">
                  Query / Message (required)
                </label>
                <textarea
                  name="queryText"
                  required
                  rows={4}
                  placeholder="Describe your issue..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowSupportModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingSupport}
                  className="px-4 py-2 text-sm font-medium text-white bg-[#9333ea] rounded-lg hover:bg-[#7e22ce] transition-colors disabled:opacity-50"
                >
                  {isSubmittingSupport ? 'Sending...' : 'Send Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerChat;
